import { useEffect, useId, useMemo, useState } from "react";
import type { FormEvent, MouseEvent, ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  Ban,
  Check as CheckIcon,
  Clock,
  Copy,
  ExternalLink,
  Loader2,
  Merge,
  EyeOff,
  Pencil,
  Pin,
  Plus,
  Radio,
  Search,
  ShieldAlert,
  Smartphone,
  Square,
  Trash2,
  X,
  type LucideIcon,
} from "lucide-react";
import type {
  StationAddToAppResult,
  StationBanKind,
  StationBans,
  StationDetail,
  StationEditableField,
  StationFilter,
  StationFinderState,
  StationGroup,
  StationListRow,
  StationMirrorResult,
} from "@gwrctl/api-client";
import { logoOrigin, type LogoOrigin } from "@gwrctl/shared";
import { client } from "../lib/client.js";
import { humanizeError } from "../lib/errors.ts";
import { useToast } from "../lib/toast.tsx";
import { PageBody, PageHeader } from "../components/Shell.tsx";
import { CountryPicker } from "../components/CountryPicker.tsx";
import { ListenButton } from "../components/ListenButton.tsx";
import {
  Banner,
  Button,
  Card,
  Check,
  Chips,
  ConfirmButton,
  Empty,
  ErrorState,
  Field,
  Modal,
  ProgressBar,
  StatusPill,
  TonePill,
  type Tone,
} from "../components/Ui.tsx";

/**
 * Raadiocast istasyon dizini: radio-browser'ın tamamı + Firestore TR kürasyonu,
 * istasyon (grup) bazında. Bir istasyonun birden çok kaynağı (yayın adresi) olur.
 *
 * Düzeltmeler radio-browser verisinin ÜSTÜNE ayrı bir katman olarak saklanır;
 * günlük senkron onları ezmez. Kürasyonlu (TR listesi) istasyonlar uygulamaya
 * Firestore'dan gider; diğer bütün ülkelerde dokunulan istasyonlar düzeltme
 * listesiyle (sunucu stations/patches.ts, uç /station-patches/raadiocast).
 * Uygulama listeyi açılışta ve en sık 30 dakikada bir çeker.
 *
 * Sağlık taraması (sunucuda ayrı süreç) kaynakları düzenli dener; 3 gün üst üste
 * kesin hata veren kaynağı otomatik gizler, düzelince geri açar.
 */

const PAGE = 50;

const FILTERS: StationFilter[] = ["all", "curated", "notinapp", "nosource", "nosourcechecked", "dead", "nologo", "rivallogo", "logoproposal", "external", "edited", "hidden", "banned"];

const FILTER_LABEL: Record<StationFilter, string> = {
  all: "Tümü",
  curated: "Kürasyon",
  notinapp: "Uygulamada yok",
  nosource: "Kaynaksız",
  nosourcechecked: "Kaynak bulunamadı",
  dead: "Açılmayan",
  autohidden: "Otomatik gizlenen",
  nologo: "Logosuz",
  rivallogo: "Rakip logosu",
  logoproposal: "Logo önerisi",
  external: "radio-polska",
  edited: "Düzenlenen",
  hidden: "Elle gizlenen",
  banned: "Yasaklanan",
};

const BY_VALUES = ["all", "auto", "pending", "manual"] as const;
const BY_LABEL: Record<(typeof BY_VALUES)[number], string> = {
  all: "Hepsi",
  auto: "Otomatik (kaynak bulucu)",
  pending: "Onay bekleyen",
  manual: "Elle",
};

/**
 * "Kaynaksız" ve "Açılmayan" içinde: yalnızca sunucumuzdan erişilemeyenler
 * (bağlantı/zaman aşımı). Yayın kullanıcıda çalışıyor olabilir; cihazda denenir.
 */
const REACH_VALUES = ["all", "unreachable"] as const;

/** Kaynağı en son kim değiştirdi: kaynak bulucu mu, konsoldan biri mi. */
function editorOf(s: Pick<StationListRow, "editedBy" | "editedAt">): { auto: boolean; label: string } | null {
  if (!s.editedBy && !s.editedAt) return null;
  const auto = s.editedBy === "kaynak-bulucu";
  const who = auto ? "otomatik · kaynak bulucu" : `elle${s.editedBy ? ` · ${s.editedBy}` : ""}`;
  return { auto, label: s.editedAt ? `${who} · ${fmtDate(s.editedAt)}` : who };
}

const COUNT_KEY: Partial<
  Record<
    StationFilter,
    "groups" | "curated" | "notInApp" | "dead" | "noSource" | "noSourceChecked" | "autoHidden" | "noLogo" | "rivalLogo" | "logoProposal" | "external" | "edited" | "hidden" | "banned"
  >
> = {
  all: "groups",
  nosource: "noSource",
  nosourcechecked: "noSourceChecked",
  curated: "curated",
  notinapp: "notInApp",
  dead: "dead",
  autohidden: "autoHidden",
  nologo: "noLogo",
  rivallogo: "rivalLogo",
  logoproposal: "logoProposal",
  external: "external",
  edited: "edited",
  hidden: "hidden",
  banned: "banned",
};

type FieldDef = {
  key: StationEditableField;
  label: string;
  mono?: boolean;
  hint?: string;
  wide?: boolean;
  numeric?: boolean;
};

const FIELDS: FieldDef[] = [
  { key: "name", label: "Ad" },
  { key: "countrycode", label: "Ülke kodu", hint: "iki harf, ör. TR" },
  { key: "url", label: "Yayın adresi", mono: true, wide: true },
  { key: "favicon", label: "Logo adresi", mono: true, wide: true },
  { key: "homepage", label: "Web sitesi", mono: true, wide: true },
  { key: "tags", label: "Türler", hint: "virgülle: pop, haber" },
  { key: "state", label: "Şehir / bölge" },
  { key: "language", label: "Dil" },
  { key: "votes", label: "Sıralama puanı", hint: "TR listesi; 9000 üstü popüler", numeric: true },
  {
    key: "group",
    label: "Grup (elle birleştir / ayır)",
    mono: true,
    wide: true,
    hint: "Boş bırakınca ada göre otomatik. Aynı değeri verdiğin kaynaklar tek istasyon olur.",
  },
];

const fmtDate = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "";

const isHiddenSrc = (s: Pick<StationListRow, "hidden" | "autoHidden" | "rbDeleted">): boolean =>
  s.hidden || s.autoHidden || s.rbDeleted;

// --- Sağlık ------------------------------------------------------------------------

interface Health {
  tone: Tone;
  icon: LucideIcon;
  label: string;
  /** Ham hata metni: yalnızca ek ayrıntı. */
  detail?: string;
}

type HealthInput = Pick<
  StationListRow,
  | "checkOk"
  | "checkError"
  | "rbCheckOk"
  | "autoHidden"
  | "checkFailStreak"
  | "certExpiresAt"
  | "deviceOkAt"
  | "deviceOkBy"
  | "deviceDeadAt"
  | "deviceDeadBy"
>;

/** Sunucuyla aynı kural (server stations/group.ts UNREACHABLE_PATTERN). */
const UNREACHABLE_RE = /zaman aşımı|timeout|ECONNRESET|socket hang up|EPROTO|bağlantı kapandı/i;

/** Sunucumuzdan erişilemiyor: yayın kullanıcıda çalışıyor olabilir, cihazda denenip onaylanabilir. */
const isUnreachable = (s: Pick<HealthInput, "checkOk" | "checkError">): boolean =>
  s.checkOk === false && UNREACHABLE_RE.test(s.checkError ?? "");

/** Ham kontrol sonucunu insan diline çevirir; tek bir anlamı olan tek bir rozet. */
function healthOf(s: HealthInput): Health {
  if (s.autoHidden) {
    return {
      tone: "idle",
      icon: EyeOff,
      label: "otomatik gizlendi",
      detail: `${s.checkFailStreak} kontrol üst üste açılmadı${s.checkError ? `: ${s.checkError}` : ""}`,
    };
  }
  if (s.deviceDeadAt) {
    return {
      tone: "danger",
      icon: X,
      label: "açılmıyor",
      detail: `Cihazda da denendi, çalışmıyor: ${s.deviceDeadBy ?? "?"}, ${fmtDate(s.deviceDeadAt)}. Uygulamaya gitmez; yayın sunucumuzdan açılırsa işaret kendiliğinden kalkar.`,
    };
  }
  if (s.checkOk === true) {
    const days = s.certExpiresAt ? Math.floor((Date.parse(s.certExpiresAt) - Date.now()) / 86_400_000) : null;
    if (days != null && days <= 14) {
      return { tone: "warn", icon: ShieldAlert, label: `sertifika ${days} gün`, detail: "HTTPS sertifikasının bitmesine az kaldı" };
    }
    return { tone: "ok", icon: CheckIcon, label: "çalışıyor" };
  }
  if (s.checkOk === false) {
    const e = s.checkError ?? "";
    const pick = (tone: Tone, icon: LucideIcon, label: string): Health => ({ tone, icon, label, detail: e || undefined });
    if (/expired/i.test(e)) return pick("danger", ShieldAlert, "sertifika dolmuş");
    if (/certificate|self.signed|altname/i.test(e)) return pick("danger", ShieldAlert, "sertifika hatası");
    if (/ENOTFOUND/i.test(e)) return pick("danger", X, "alan adı yok");
    if (/ECONNREFUSED/i.test(e)) return pick("danger", X, "bağlantı reddedildi");
    // Bağlantı kurulamadı/zaman aşımı: bazı yayın sunucuları veri merkezi IP'lerini
    // engelliyor; yayın kullanıcıda çalışıyor olabilir. "Ölü" demiyoruz.
    if (isUnreachable(s)) {
      if (s.deviceOkAt) {
        return {
          tone: "ok",
          icon: Smartphone,
          label: "cihazdan erişiliyor",
          detail: `Sunucumuzdan erişilemiyor (${e}) ama cihazda denendi, çalışıyor: ${s.deviceOkBy ?? "?"}, ${fmtDate(s.deviceOkAt)}. Yayın gerçekten ölürse (alan adı yok, 404…) onay düşer.`,
        };
      }
      return {
        tone: "warn",
        icon: Clock,
        label: "sunucumuzdan erişilemiyor",
        detail: `${e || "bağlantı kurulamadı"}. Bazı yayın sunucuları veri merkezlerini engeller; tarayıcıda deneyip kontrol et.`,
      };
    }
    if (/HTTP (403|451)/.test(e)) return pick("warn", Ban, "erişim engelli");
    if (/web sayfası/i.test(e)) return pick("danger", X, "yayın değil, sayfa");
    const http = /HTTP (\d+)/.exec(e);
    if (http) return pick("danger", X, `HTTP ${http[1]}`);
    return pick("danger", X, "açılmıyor");
  }
  if (s.rbCheckOk === false) {
    return { tone: "idle", icon: X, label: "radio-browser: bozuk", detail: "Henüz bizim kontrolümüzden geçmedi" };
  }
  return { tone: "idle", icon: Clock, label: "kontrol edilmedi" };
}

/**
 * "Sunucumuzdan erişilemiyor" kaynak için cihazda deneme sonucu:
 *  - Cihazda çalışıyor: kaynak kalır, "Açılmayan" ve "Kaynaksız"dan düşer.
 *  - Cihazda da çalışmıyor: kaynak "açılmıyor" rozetiyle "Açılmayan"da, istasyon
 *    "Kaynaksız"da kalır ama kaynak uygulamaya gitmez. İstasyonu "Kaynak bulunamadı"ya
 *    taşımak istasyon düzeyindeki düğmenin işi (NoSourceButtons).
 */
function DeviceVerdictButtons({
  s,
}: {
  s: HealthInput & Pick<StationListRow, "id" | "url" | "hidden">;
}): ReactNode {
  const toast = useToast();
  const queryClient = useQueryClient();
  const set = useMutation({
    mutationFn: (verdict: "ok" | "dead" | "undo") =>
      verdict === "dead"
        ? client.updateStation(s.id, { deviceDead: true })
        : s.deviceDeadAt
          ? client.updateStation(s.id, { deviceDead: false })
          : client.updateStation(s.id, { deviceOk: verdict === "ok" }),
    onSuccess: (_res, verdict) => {
      toast({
        tone: "ok",
        title:
          verdict === "ok"
            ? "Cihazdan erişiliyor olarak işaretlendi"
            : verdict === "dead"
              ? "Açılmıyor olarak işaretlendi"
              : "İşaret kaldırıldı",
        body:
          verdict === "ok"
            ? "Kontrol edilecekler listesinden düştü"
            : verdict === "dead"
              ? "Uygulamaya gitmez; Açılmayan ve Kaynaksız'da kalır. İstasyonun hiç kaynağı yoksa Kaynak bulunamadı düğmesini kullan."
              : s.url,
      });
      void queryClient.invalidateQueries({ queryKey: ["station"] });
      void queryClient.invalidateQueries({ queryKey: ["stations"] });
      void queryClient.invalidateQueries({ queryKey: ["stations-state"] });
    },
    onError: (err) => toast({ tone: "danger", title: "Kaydedilemedi", body: humanizeError(err) }),
  });
  if (s.hidden || (!isUnreachable(s) && !s.deviceDeadAt)) return null;
  const busy = (v: "ok" | "dead" | "undo"): boolean => set.isPending && set.variables === v;
  if (s.deviceOkAt || s.deviceDeadAt) {
    return (
      <Button
        size="sm"
        loading={busy("undo")}
        disabled={set.isPending}
        onClick={() => set.mutate("undo")}
        title="İşareti kaldır: kaynak yeniden kontrol edilecekler listesine döner"
      >
        {s.deviceDeadAt ? "Geri al" : "Onayı kaldır"}
      </Button>
    );
  }
  return (
    <span className="flex shrink-0 items-center gap-2">
      <Button
        size="sm"
        variant="primary"
        icon={Smartphone}
        loading={busy("ok")}
        disabled={set.isPending}
        onClick={() => set.mutate("ok")}
        title="Yayını cihazda (telefon/tarayıcı) denedim, çalışıyor"
      >
        Cihazda çalışıyor
      </Button>
      <Button
        size="sm"
        variant="danger"
        icon={X}
        loading={busy("dead")}
        disabled={set.isPending}
        onClick={() => set.mutate("dead")}
        title="Cihazda da açılmıyor: bu kaynak uygulamaya gitmesin (istasyon Kaynaksız'da kalır)"
      >
        Cihazda da çalışmıyor
      </Button>
    </span>
  );
}

/**
 * Kaynaksız istasyon: "kontrol edildi, kaynak bulunamadı" (çalışmayan bütün
 * kaynaklar "cihazda da çalışmıyor" olur, istasyon uygulamadan kalkar) ya da geri al.
 * Yalnızca bu düğme istasyonu "Kaynak bulunamadı"ya taşır.
 */
function NoSourceButtons({ groupKey, name, checked }: { groupKey: string; name: string; checked: boolean }): ReactNode {
  const toast = useToast();
  const queryClient = useQueryClient();
  const mark = useMutation({
    mutationFn: () => client.markStationNoSource(groupKey, !checked),
    onSuccess: () => {
      toast({
        tone: "ok",
        title: checked ? `${name} Kaynaksız'a döndü` : `${name}: kaynak bulunamadı`,
        body: checked ? "Yeniden kontrol edilecekler arasında" : "Kaynak bulunamadı listesine geçti, uygulamada gösterilmez",
      });
      void queryClient.invalidateQueries({ queryKey: ["station"] });
      void queryClient.invalidateQueries({ queryKey: ["stations"] });
      void queryClient.invalidateQueries({ queryKey: ["stations-state"] });
    },
    onError: (err) => toast({ tone: "danger", title: "Kaydedilemedi", body: humanizeError(err) }),
  });
  return checked ? (
    <Button size="sm" loading={mark.isPending} onClick={() => mark.mutate()} title="Kaynaksız listesine geri al">
      Geri al
    </Button>
  ) : (
    <Button
      size="sm"
      variant="danger"
      icon={X}
      loading={mark.isPending}
      onClick={() => mark.mutate()}
      title="Kontrol ettim, çalışan kaynağı yok (kapanmış ya da yayın yok): uygulamada gösterme"
    >
      Kaynak bulunamadı
    </Button>
  );
}

function HealthPill({ s }: { s: HealthInput }): ReactNode {
  const h = healthOf(s);
  return (
    <TonePill tone={h.tone} icon={h.icon} title={h.detail}>
      {h.label}
    </TonePill>
  );
}

// --- Logo --------------------------------------------------------------------------

/** Logo rakip/dizin sunucusundaysa satırda uyarı (bkz. @gwrctl/shared logos.ts). */
function RivalLogoTag({ origin }: { origin: LogoOrigin | null }): ReactNode {
  if (origin?.kind !== "rival") return null;
  return (
    <span
      className="tag tag-outline"
      style={{ color: "var(--color-warn)", borderColor: "var(--color-warn)" }}
      title="Logo bir rakip uygulamanın ya da radyo dizininin sunucusunda; istasyonun kendi logosuyla değiştirilmeli"
    >
      logo: {origin.name}
    </span>
  );
}

const ORIGIN_TEXT: Record<LogoOrigin["kind"], (o: LogoOrigin) => string> = {
  rival: (o) => `Kaynak: ${o.name} sunucusu. Rakip/dizin sunucusu; istasyonun kendi logosuyla değiştirilmeli.`,
  own: (o) => `Kaynak: istasyonun kendi sitesi (${o.name}).`,
  wiki: () => "Kaynak: Wikimedia.",
  other: (o) => `Kaynak: ${o.name} (istasyonun sitesi değil; kim yüklediği belli değil).`,
};

function LogoOriginNote({ origin }: { origin: LogoOrigin | null }): ReactNode {
  if (!origin) return null;
  return <span className={origin.kind === "rival" ? "text-warn" : undefined}>{ORIGIN_TEXT[origin.kind](origin)}</span>;
}

function useInvalidateStations(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["station"] });
    void queryClient.invalidateQueries({ queryKey: ["stations"] });
    void queryClient.invalidateQueries({ queryKey: ["stations-state"] });
  };
}

/** Kaynak bulucunun emin olamayıp gizli eklediği kaynak (sunucuda FINDER_PENDING). */
const isFinderPending = (s: Pick<StationListRow, "hidden" | "hiddenReason">): boolean =>
  s.hidden && (s.hiddenReason ?? "").startsWith("kaynak bulucu");

/** Satırdan: kaynak bulucunun onaya bıraktığı adresi onayla (açılır) ya da sil. */
function FinderPendingButtons({ src }: { src: Pick<StationListRow, "id" | "url"> }): ReactNode {
  const toast = useToast();
  const invalidate = useInvalidateStations();
  const act = useMutation({
    mutationFn: (approve: boolean) => (approve ? client.updateStation(src.id, { hidden: false }) : client.deleteStation(src.id)),
    onSuccess: (_r, approve) => {
      toast({ tone: "ok", title: approve ? "Kaynak onaylandı" : "Kaynak silindi", body: src.url });
      invalidate();
    },
    onError: (err) => toast({ tone: "danger", title: "Kaydedilemedi", body: humanizeError(err) }),
  });
  return (
    <span className="flex shrink-0 items-center gap-2">
      <Button
        size="sm"
        variant="primary"
        icon={CheckIcon}
        loading={act.isPending && act.variables === true}
        disabled={act.isPending}
        onClick={() => act.mutate(true)}
        title="Bu adres gerçekten bu istasyonun yayını: görünür olur ve uygulamaya gider"
      >
        Onayla
      </Button>
      <Button
        size="sm"
        variant="danger"
        icon={Trash2}
        loading={act.isPending && act.variables === false}
        disabled={act.isPending}
        onClick={() => act.mutate(false)}
        title="Başka istasyonun yayını: kaynağı sil (bulucu bir daha eklemez)"
      >
        Sil
      </Button>
    </span>
  );
}

/** Sunucudaki stations/radiopolska.ts PENDING_REASON ile aynı. */
const RP_PENDING = "radio-polska: onay bekliyor";

/** radio-polska'dan gelen, onay bekleyen istasyon: uygulamaya al / alma. */
function ExternalReviewButtons({ groupKey, name }: { groupKey: string; name: string }): ReactNode {
  const toast = useToast();
  const invalidate = useInvalidateStations();
  const review = useMutation({
    mutationFn: (approve: boolean) => client.reviewExternalStation(groupKey, approve),
    onSuccess: (_r, approve) => {
      toast({
        tone: "ok",
        title: approve ? `${name} uygulamaya alındı` : `${name} alınmadı`,
        body: approve ? "Yayını açılıyorsa uygulamanın yeni sürümünde ek istasyon olarak görünür" : "Sonraki içe aktarmada geri gelmez",
      });
      invalidate();
    },
    onError: (err) => toast({ tone: "danger", title: "Kaydedilemedi", body: humanizeError(err) }),
  });
  return (
    <span className="flex shrink-0 items-center gap-2">
      <Button
        size="sm"
        variant="primary"
        icon={CheckIcon}
        loading={review.isPending && review.variables === true}
        disabled={review.isPending}
        onClick={() => review.mutate(true)}
      >
        Uygulamaya al
      </Button>
      <Button
        size="sm"
        variant="danger"
        icon={X}
        loading={review.isPending && review.variables === false}
        disabled={review.isPending}
        onClick={() => review.mutate(false)}
      >
        Alma
      </Button>
    </span>
  );
}

/** Bekleyen logo önerisi: şimdiki (rakip sunucusundaki) → önerilen, kabul / ret. */
function LogoProposalBlock({ row, size = 40 }: { row: Pick<StationListRow, "id" | "logoProposal">; size?: number }): ReactNode {
  const toast = useToast();
  const invalidate = useInvalidateStations();
  const decide = useMutation({
    mutationFn: (accept: boolean) => client.decideLogoProposal(row.id, accept),
    onSuccess: (_r, accept) => {
      toast({ tone: "ok", title: accept ? "Logo değiştirildi" : "Öneri reddedildi", body: accept ? "Uygulamaya gider" : "Bu logo bir daha önerilmez" });
      invalidate();
    },
    onError: (err) => toast({ tone: "danger", title: "Kaydedilemedi", body: humanizeError(err) }),
  });
  const p = row.logoProposal;
  if (p?.status !== "pending" || !p.url) return null;
  return (
    <span className="flex shrink-0 items-center gap-2" title={`Önerilen logo: ${p.url} (${p.from ?? "?"}, ${p.homepage ?? ""})`}>
      <span className="text-2xs text-neutral-600" aria-hidden>
        →
      </span>
      <a href={p.url} target="_blank" rel="noreferrer noopener">
        <Logo src={p.url} size={size} />
      </a>
      <span className="flex items-center gap-2">
        <Button
          size="sm"
          variant="primary"
          icon={CheckIcon}
          loading={decide.isPending && decide.variables === true}
          disabled={decide.isPending}
          onClick={() => decide.mutate(true)}
        >
          Kabul et
        </Button>
        <Button
          size="sm"
          icon={X}
          loading={decide.isPending && decide.variables === false}
          disabled={decide.isPending}
          onClick={() => decide.mutate(false)}
        >
          Reddet
        </Button>
      </span>
    </span>
  );
}

/** Tek istasyon: logosu rakip sunucusundaysa kendi sitesinde logo ara. */
function FindLogoButton({ id }: { id: string }): ReactNode {
  const toast = useToast();
  const invalidate = useInvalidateStations();
  const find = useMutation({
    mutationFn: () => client.findStationLogo(id),
    onSuccess: (res) => {
      const p = res.proposal;
      toast({
        tone: p.status === "pending" ? "ok" : "danger",
        title: p.status === "pending" ? "Kendi logosu bulundu" : "Sitesinde logo bulunamadı",
        body: p.status === "pending" ? "Önerilen logoyu kabul ya da reddet" : (p.homepage ?? "web sitesi yok"),
      });
      invalidate();
    },
    onError: (err) => toast({ tone: "danger", title: "Aranamadı", body: humanizeError(err) }),
  });
  return (
    <button
      type="button"
      className="inline-flex w-fit items-center gap-1 text-2xs text-accent-300 hover:underline disabled:opacity-50"
      disabled={find.isPending}
      onClick={() => find.mutate()}
    >
      {find.isPending ? <Loader2 size={11} className="animate-spin" aria-hidden /> : <Search size={11} aria-hidden />}
      istasyonun sitesinde logo ara
    </button>
  );
}

/** "Rakip logosu" filtresinde: toplu logo arama (seçili ülke) ve durumu. */
function LogoFinderPanel({ country, state }: { country: string | null; state: Record<string, unknown> | undefined }): ReactNode {
  const toast = useToast();
  const invalidate = useInvalidateStations();
  const start = useMutation({
    mutationFn: () => client.startLogoFinder({ country, limit: 200 }),
    onSuccess: () => {
      toast({ tone: "ok", title: "Logo arama başladı", body: `${country ?? "tüm ülkeler"} · en fazla 200 istasyon, siteler yavaşça gezilir` });
      invalidate();
    },
    onError: (err) => toast({ tone: "danger", title: "Başlatılamadı", body: humanizeError(err) }),
  });
  const stop = useMutation({ mutationFn: () => client.stopLogoFinder(), onSuccess: invalidate });
  const running = state?.status === "running";
  const n = (v: unknown): string => Number(v ?? 0).toLocaleString("tr-TR");
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs">
      {running ? (
        <Button size="sm" loading={stop.isPending} onClick={() => stop.mutate()}>
          Durdur
        </Button>
      ) : (
        <Button size="sm" icon={Search} loading={start.isPending} onClick={() => start.mutate()}>
          {country ? `${country} için kendi logolarını ara` : "Kendi logolarını ara"}
        </Button>
      )}
      <span role="status" className="text-neutral-500">
        {running && `${n(state?.done)} / ${n(state?.total)} istasyon · ${n(state?.found)} öneri`}
        {state?.status === "ok" &&
          `Son arama ${fmtDate(String(state.finishedAt))}${state.country ? ` (${String(state.country)})` : ""}: ${n(state.done)} istasyon, ${n(state.found)} öneri`}
        {(state?.status === "error" || state?.status === "paused") && String(state.error)}
        {state?.status === "stopped" && `Durduruldu: ${n(state.done)} / ${n(state.total)}`}
      </span>
    </div>
  );
}

function Logo({ src, broken = false, size = 40 }: { src: string | null; broken?: boolean; size?: number }): ReactNode {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  const box = { width: size, height: size };
  if (!src || failed || broken) {
    const isBroken = !!src && (failed || broken);
    return (
      <span
        className="grid shrink-0 place-items-center rounded-[var(--radius-sm)] bg-neutral-900"
        style={{
          ...box,
          border: isBroken ? "1px dashed color-mix(in srgb, var(--color-warn) 55%, transparent)" : undefined,
        }}
        title={isBroken ? "logo yüklenemedi" : "logo yok"}
      >
        <Radio size={Math.round(size * 0.45)} className="text-neutral-700" aria-hidden />
      </span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      className="shrink-0 rounded-[var(--radius-sm)] object-contain"
      style={{ ...box, background: "rgba(255,255,255,0.04)" }}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

// --- Sayfa -------------------------------------------------------------------------

export function StationsPage(): ReactNode {
  const toast = useToast();
  const queryClient = useQueryClient();
  // Filtreler adreste: bağlantıyla paylaşılır, geri tuşu çalışır.
  const [params, setParams] = useSearchParams();
  // Ülke yoksa TR; "Tüm ülkeler" adreste "all" (boş değer silinip TR'ye dönerdi).
  const countryParam = params.get("country") ?? "TR";
  const country = countryParam === "all" ? "" : countryParam;
  const filter = (FILTERS.includes(params.get("filter") as StationFilter) ? params.get("filter") : "all") as StationFilter;
  const page = Math.max(0, Number(params.get("page") ?? 0) || 0);
  const byParam = params.get("by");
  const by = filter === "edited" && (byParam === "auto" || byParam === "manual" || byParam === "pending") ? byParam : undefined;
  const reach =
    (filter === "dead" || filter === "nosource") && params.get("reach") === "unreachable" ? ("unreachable" as const) : undefined;
  const openId = params.get("station");
  const [search, setSearch] = useState(params.get("q") ?? "");
  const q = params.get("q") ?? "";

  const update = (patch: Record<string, string | null>, resetPage = true): void => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === "") next.delete(k);
      else next.set(k, v);
    }
    if (resetPage && !("page" in patch)) next.delete("page");
    setParams(next, { replace: "q" in patch });
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== q) update({ q: search.trim() || null });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const state = useQuery({
    queryKey: ["stations-state", country],
    queryFn: () => client.stationState(country || undefined),
    refetchInterval: (s) =>
      s.state.data?.sync.rbFull?.status === "running" ||
      s.state.data?.sync.chunklistClean?.status === "running" ||
      s.state.data?.sync.finder?.status === "running" ||
      s.state.data?.sync.rpImport?.status === "running" ||
      s.state.data?.sync.logoFinder?.status === "running"
        ? 5000
        : 60_000,
  });

  const list = useQuery({
    queryKey: ["stations", country, filter, by, reach, q, page],
    queryFn: () =>
      client.stations({ country: country || undefined, filter, by, reach, q: q || undefined, limit: PAGE, offset: page * PAGE }),
    placeholderData: keepPreviousData,
  });

  const sync = useMutation({
    mutationFn: (what: SyncWhat): Promise<{ started?: boolean; result?: Record<string, unknown> }> =>
      what === "chunklist" ? client.cleanStationChunklists() : client.syncStations(what),
    onSuccess: (res, what) => {
      toast({
        tone: "ok",
        title:
          what === "curated"
            ? "TR listesi içe aktarıldı"
            : what === "chunklist"
              ? res.started === false
                ? "Chunklist temizliği zaten çalışıyor"
                : "Chunklist temizliği başladı"
              : what === "radio-polska"
                ? res.started === false
                  ? "radio-polska içe aktarma zaten çalışıyor"
                  : "radio-polska içe aktarma başladı"
                : "radio-browser senkronu başladı",
        body: res.result
          ? `${String(res.result.imported)} istasyon`
          : what === "chunklist"
            ? "her adresin ana listesi denenir, birkaç dakika sürer"
            : what === "radio-polska"
              ? "sayfalar yavaşça gezilir, yarım saat kadar sürer"
              : "yaklaşık bir dakika sürer",
      });
      void queryClient.invalidateQueries({ queryKey: ["stations-state"] });
      void queryClient.invalidateQueries({ queryKey: ["stations"] });
    },
    onError: (err) => toast({ tone: "danger", title: "Başlatılamadı", body: humanizeError(err) }),
  });

  const bans = useQuery({ queryKey: ["station-bans"], queryFn: () => client.stationBans() });
  const { ban: banSelected } = useBanActions();
  const addToApp = useAddToApp();

  // Seçilen istasyonları tek istasyon yap: Olası kopyalar'daki birleştirmeyle aynı uç.
  const mergeSelected = useMutation({
    mutationFn: (keys: string[]) => client.mergeStationDuplicates(keys),
    onSuccess: (res, keys) => {
      setSelected(new Map());
      void queryClient.invalidateQueries({ queryKey: ["stations"] });
      void queryClient.invalidateQueries({ queryKey: ["stations-state"] });
      void queryClient.invalidateQueries({ queryKey: ["station-duplicates"] });
      const summary = `${keys.length} istasyon tek oldu · toplam ${res.sources} kaynak`;
      toast({
        tone: res.firestore.ok === false ? "danger" : "ok",
        title: "Seçilenler birleştirildi",
        body: res.firestore.ok === false ? `${summary}; TR listesine yansıtılamadı: ${res.firestore.error ?? ""}` : summary,
      });
    },
    onError: (err) => toast({ tone: "danger", title: "Birleştirilemedi", body: humanizeError(err) }),
  });

  // Toplu arama için seçilen istasyonlar (grup anahtarı -> ad); sayfalar arası korunur.
  const [selected, setSelected] = useState<Map<string, string>>(() => new Map());
  const toggleSelected = (key: string, name: string, on: boolean): void =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (on) next.set(key, name);
      else next.delete(key);
      return next;
    });

  const findBatch = useMutation({
    mutationFn: (groupKeys: string[] | null) =>
      client.findSources(groupKeys ? { groupKeys } : { country: country || null, limit: 300 }),
    onSuccess: (_res, groupKeys) => {
      toast({
        tone: "ok",
        title: groupKeys ? `${groupKeys.length} istasyon için kaynak arama başladı` : "Kaynak arama başladı",
        body: "Siteleri yormamak için yavaş ilerler; sayfadan çıksan da sürer, durumu en üstte görünür",
      });
      if (groupKeys) setSelected(new Map());
      void queryClient.invalidateQueries({ queryKey: ["stations-state"] });
    },
    onError: (err) => toast({ tone: "danger", title: "Başlatılamadı", body: humanizeError(err) }),
  });

  const stopFind = useMutation({
    mutationFn: () => client.stopFindSources(),
    onSuccess: (res) => {
      toast({ tone: "ok", title: res.stopping ? "Durduruluyor" : "Durduruldu", body: res.stopping ? "Eldeki istasyon bitince durur" : undefined });
      void queryClient.invalidateQueries({ queryKey: ["stations-state"] });
    },
    onError: (err) => toast({ tone: "danger", title: "Durdurulamadı", body: humanizeError(err) }),
  });

  // Arama bitince (çalışıyor -> başka durum) liste yeni kaynaklarla tazelensin.
  const finder = state.data?.sync.finder as StationFinderState | undefined;
  const [lastFinderStatus, setLastFinderStatus] = useState(finder?.status);
  useEffect(() => {
    if (lastFinderStatus === "running" && finder?.status !== "running") {
      void queryClient.invalidateQueries({ queryKey: ["stations"] });
    }
    setLastFinderStatus(finder?.status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finder?.status]);

  const total = list.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));
  const counts = state.data?.counts;
  const labels = useMemo(() => {
    const out: Partial<Record<StationFilter, string>> = {};
    for (const f of FILTERS) {
      const key = COUNT_KEY[f];
      const n = key && counts ? counts[key] : null;
      out[f] = n != null ? `${FILTER_LABEL[f]} · ${n.toLocaleString("tr-TR")}` : FILTER_LABEL[f];
    }
    return out;
  }, [counts]);

  const from = total ? page * PAGE + 1 : 0;
  const to = Math.min(total, (page + 1) * PAGE);

  return (
    <>
      <PageHeader kicker="raadiocast" title="İstasyonlar" />

      <PageBody>
        {state.data && !state.data.firestore && (
          <Banner tone="warn" kicker="firestore">
            Firestore yapılandırılmamış; TR listesindeki düzenlemeler uygulamaya yansımaz.
          </Banner>
        )}

        <FinderCard
          finder={finder}
          auto={state.data?.sync.finderAuto as { lastAt?: string; everyMs?: number; limit?: number } | undefined}
          country={country}
          noSource={counts?.noSource}
          starting={findBatch.isPending}
          stopping={stopFind.isPending}
          onFindNoSource={() => findBatch.mutate(null)}
          onStop={() => stopFind.mutate()}
          onPick={(name) => setSearch(name)}
        />

        <Card
          title={list.data ? `${total.toLocaleString("tr-TR")} istasyon` : "İstasyonlar"}
          hint="aynı istasyonun kaynakları tek satırda"
          action={
            <>
              <CountryPicker
                value={country}
                onChange={(c) => update({ country: c || "all" })}
                options={state.data?.countries ?? []}
              />
              <input
                type="search"
                className="input w-full sm:!w-64"
                aria-label="Ad, adres ya da kimlik ara"
                placeholder="ad, adres ya da kimlik ara"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </>
          }
          flush
        >
          <div className="grid gap-2 px-4 pt-3 pb-2">
            <Chips
              aria-label="Filtre"
              values={FILTERS.filter(
                (f) => (f !== "notinapp" || country === "TR") && (f !== "external" || !country || country === "PL"),
              )}
              value={filter}
              onChange={(f) => update({ filter: f === "all" ? null : f, by: null, reach: null })}
              labels={labels}
            />
            {(filter === "dead" || filter === "nosource") && (
              <Chips
                aria-label="Hata türü"
                values={REACH_VALUES}
                value={reach ?? "all"}
                onChange={(r) => update({ reach: r === "all" ? null : r })}
                labels={{
                  all: "Hepsi",
                  unreachable: (() => {
                    const n = filter === "dead" ? counts?.deadUnreachable : counts?.noSourceUnreachable;
                    return n != null ? `Sunucumuzdan erişilemiyor · ${n.toLocaleString("tr-TR")}` : "Sunucumuzdan erişilemiyor";
                  })(),
                }}
              />
            )}
            {filter === "edited" && (
              <>
                <Chips
                  aria-label="Düzenleyen"
                  values={BY_VALUES}
                  value={by ?? "all"}
                  onChange={(b) => update({ by: b === "all" ? null : b })}
                  labels={{
                    ...BY_LABEL,
                    pending: counts ? `${BY_LABEL.pending} · ${counts.finderPending.toLocaleString("tr-TR")}` : BY_LABEL.pending,
                  }}
                />
                <p className="text-xs text-neutral-500">
                  En son düzenlenen üstte. "Otomatik": kaynak bulucunun eklediği adresler; "Onay bekleyen": bulucunun emin
                  olamayıp gizli eklediği adresler (istasyonu açıp kaynağın yanındaki "onayla" ile açılır); "Elle": konsoldan
                  yapılan düzeltmeler.
                </p>
              </>
            )}
            {filter === "nosource" && (
              <p className="text-xs text-neutral-500">
                Hiçbir kaynağı açılmayan istasyonlar. İstasyonu açıp "Kaynak ara" ile, seçip toplu ya da yukarıdaki aramayla yeni
                yayın adresi aranır: ölü adresin varyasyonları, istasyonun sitesi, (TR) canliradyodinle.fm ve (PL) radio-polska.pl. Yalnızca açılan
                adresler eklenir.
              </p>
            )}
            {filter === "external" && (
              <p className="text-xs text-neutral-500">
                radio-polska.pl'den içe aktarılan, radio-browser'da olmayan PL istasyonları
                {counts ? ` (${counts.externalPending.toLocaleString("tr-TR")} onay bekliyor)` : ""}. Onay bekleyenler
                gizlidir, uygulamaya gitmez. "Uygulamaya al" açar (yayını açılanlar uygulamanın yeni sürümüne ek istasyon
                olarak gider); "Alma" kalıcı düşürür, sonraki içe aktarma geri getirmez. Turuncu not: radio-browser'da
                benzer adlı istasyon var, kopya olabilir.
              </p>
            )}
            {filter === "rivallogo" && (
              <>
                <p className="text-xs text-neutral-500">
                  Logosu bir rakip uygulamanın ya da radyo dizininin sunucusunda olan istasyonlar (TuneIn, iHeart, radio.net,
                  myTuner…; ayrıca Google görsel önizlemeleri ve uygulama mağazası ikonları). Her gösterimde onların bant
                  genişliği kullanılır ve dinleyicinin IP'si onlara gider. Logo arama istasyonların kendi sitesinde logo
                  arar; bulduklarını değiştirmez, "Logo önerisi"nde onayına sunar.
                </p>
                <LogoFinderPanel country={country || null} state={state.data?.sync.logoFinder} />
              </>
            )}
            {filter === "logoproposal" && (
              <p className="text-xs text-neutral-500">
                Logo arayıcının istasyonun kendi sitesinde bulduğu logolar. Solda şimdiki (rakip sunucusundaki), sağda
                önerilen. Kabul edilen logo istasyonun tüm kaynaklarına yazılır ve uygulamaya gider; reddedilen bir daha
                önerilmez.
              </p>
            )}
            {filter === "nosourcechecked" && (
              <p className="text-xs text-neutral-500">
                Kontrol edilmiş, çalışan kaynağı bulunamayan istasyonlar (kapanmış ya da yayını yok). Uygulamada
                gösterilmezler. Kaynak bulucu ara sıra yine dener; açılan bir adres bulursa istasyon kendiliğinden
                normale döner. "Geri al" Kaynaksız'a döndürür.
              </p>
            )}
            {filter === "banned" && <BansPanel bans={bans.data} />}
            {filter === "notinapp" && (
              <p className="text-xs text-neutral-500">
                Yalnızca radio-browser'da olan, uygulamanın TR listesinde olmayan istasyonlar (uygulama TR'de yalnızca o
                listeyi gösterir). Uygun gördüklerini seçip "Uygulamaya ekle"ye bas ya da istasyonu açıp ekle. Eklenen
                istasyon arama ve türlerde çıkar; ana ekrandaki popülerlere girmesi için "Sıralama puanı"nı 9000 üstü yap.
              </p>
            )}
            {filter === "autohidden" && (
              <p className="text-xs text-neutral-500">
                Sağlık taraması 3 gün üst üste kesin hata veren kaynakları gizler; yayın düzelince kendiliğinden geri
                açılır. 403 ve zaman aşımı coğrafi engel olabileceği için sayılmaz.
              </p>
            )}
          </div>
          {list.data?.groups.length ? (
            <SelectionBar
              pageGroups={list.data.groups}
              selected={selected}
              onChange={setSelected}
              busy={findBatch.isPending}
              blocked={finder?.status === "running" && !finderStale(finder)}
              onFind={() => findBatch.mutate([...selected.keys()])}
              canAdd={country === "TR"}
              adding={addToApp.isPending}
              onAdd={() => addToApp.mutate([...selected.keys()], { onSuccess: () => setSelected(new Map()) })}
              merging={mergeSelected.isPending}
              onMerge={() => mergeSelected.mutate([...selected.keys()])}
              banning={banSelected.isPending}
              onBan={() =>
                banSelected.mutate(
                  { kind: "names", values: [...new Set(selected.values())] },
                  { onSuccess: () => setSelected(new Map()) },
                )
              }
            />
          ) : null}
          <div className="h-[2px]">{list.isFetching && !list.isLoading && <ProgressBar />}</div>
          <p className="sr-only" role="status">
            {list.data ? `${total} istasyon${pages > 1 ? `, sayfa ${page + 1} / ${pages}` : ""}` : ""}
          </p>

          {list.isError ? (
            <ErrorState error={list.error} onRetry={() => void list.refetch()} />
          ) : list.isLoading ? (
            <Empty>Yükleniyor…</Empty>
          ) : list.data?.groups.length ? (
            <>
              <ul role="list" className="station-list">
                {list.data.groups.map((g) => (
                  <li key={g.key}>
                    <GroupRow
                      g={g}
                      filter={filter}
                      onOpen={(id) => update({ station: id }, false)}
                      selected={selected.has(g.key)}
                      onSelect={(on) => toggleSelected(g.key, g.primary.name, on)}
                    />
                  </li>
                ))}
              </ul>
              {pages > 1 && (
                <nav aria-label="Sayfalama" className="row items-center justify-between">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Önceki sayfa"
                    disabled={page === 0}
                    onClick={() => update({ page: String(page - 1) }, false)}
                  >
                    <span aria-hidden>←</span> Önceki
                  </Button>
                  <span className="mono text-2xs text-neutral-600">
                    {from.toLocaleString("tr-TR")}–{to.toLocaleString("tr-TR")} / {total.toLocaleString("tr-TR")}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Sonraki sayfa"
                    disabled={page + 1 >= pages}
                    onClick={() => update({ page: String(page + 1) }, false)}
                  >
                    Sonraki <span aria-hidden>→</span>
                  </Button>
                </nav>
              )}
            </>
          ) : (
            <Empty>
              {q
                ? `“${q}” ile eşleşen istasyon yok`
                : counts?.total
                  ? "Bu filtrede istasyon yok"
                  : "Dizin boş: TR listesini içe aktar ve radio-browser senkronunu başlat"}
            </Empty>
          )}
        </Card>

        <DirectoryCard
          sync={state.data?.sync}
          counts={counts}
          busy={sync.isPending ? sync.variables : null}
          onSync={(what) => sync.mutate(what)}
        />
      </PageBody>

      {openId && (
        <StationModal
          id={openId}
          onOpen={(id) => update({ station: id }, false)}
          onClose={() => update({ station: null }, false)}
        />
      )}
    </>
  );
}

// --- Kaynak arama ------------------------------------------------------------------

/** Sunucu yeniden başladıysa durum "çalışıyor" kalır; 15 dk güncellenmediyse yarıda kalmıştır. */
function finderStale(f: StationFinderState): boolean {
  return f.status === "running" && !!f.updatedAt && Date.now() - Date.parse(f.updatedAt) > 15 * 60_000;
}

const FINDER_PILL: Record<StationFinderState["status"] | "stale", { tone: Tone; icon: LucideIcon; label: string }> = {
  running: { tone: "accent", icon: Loader2, label: "çalışıyor" },
  ok: { tone: "ok", icon: CheckIcon, label: "bitti" },
  paused: { tone: "warn", icon: Clock, label: "site yavaşlattı, durdu" },
  stopped: { tone: "idle", icon: Square, label: "durduruldu" },
  error: { tone: "danger", icon: X, label: "hata" },
  stale: { tone: "warn", icon: Clock, label: "yarıda kaldı" },
};

const RESULTS_SHOWN = 8;

/**
 * Toplu kaynak aramanın durumu. Durum sunucuda (DB) tutulur: arama sayfadan
 * çıkınca da sürer, geri gelince son durum ve bulunanlar burada görünür.
 */
function FinderCard({
  finder,
  auto,
  country,
  noSource,
  starting,
  stopping,
  onFindNoSource,
  onStop,
  onPick,
}: {
  finder: StationFinderState | undefined;
  auto: { lastAt?: string; everyMs?: number; limit?: number } | undefined;
  country: string;
  noSource: number | undefined;
  starting: boolean;
  stopping: boolean;
  onFindNoSource: () => void;
  onStop: () => void;
  onPick: (name: string) => void;
}): ReactNode {
  const [showAll, setShowAll] = useState(false);
  const stale = !!finder && finderStale(finder);
  const running = finder?.status === "running" && !stale;
  const pill = finder ? (FINDER_PILL[stale ? "stale" : finder.status] ?? FINDER_PILL.error) : null;
  const done = finder?.done ?? 0;
  const total = finder?.total ?? 0;
  // Bulunanlar önce, bulunamayanlar arkada; kendi içinde en yeni önde.
  const sorted = [...(finder?.results ?? [])].sort(
    (a, b) => Number(b.added + b.review > 0) - Number(a.added + a.review > 0),
  );
  const shown = showAll ? sorted : sorted.slice(0, RESULTS_SHOWN);
  const scope = [
    finder?.trigger === "auto" ? "otomatik" : "elle",
    finder?.mode === "selected" ? "seçilen istasyonlar" : `kaynaksızlar${finder?.country ? ` (${finder.country})` : " (tüm ülkeler)"}`,
  ].join(" · ");
  const nextAuto = auto?.lastAt && auto.everyMs ? new Date(Date.parse(auto.lastAt) + auto.everyMs).toISOString() : null;
  const autoLine = auto?.everyMs
    ? `Otomatik: her ${Math.round(auto.everyMs / 3_600_000)} saatte bir, kaynaksızlardan ${String(auto.limit ?? "")} istasyon (son 7 günde denenenler atlanır)${nextAuto ? ` · sonraki ${fmtDate(nextAuto)} sonrası` : ""}`
    : "Otomatik arama sunucu açıldıktan kısa süre sonra başlar, sonra belirli aralıklarla tekrarlar.";
  const findLabel = [country, noSource != null ? noSource.toLocaleString("tr-TR") : null].filter(Boolean).join(" · ");

  return (
    <Card
      title="Kaynak arama"
      icon={Search}
      hint="ölü adresin varyasyonları, istasyon sitesi, canliradyodinle.fm ve radio-polska.pl; yalnızca açılan adresler eklenir"
      action={
        running || stale ? (
          <Button size="sm" icon={Square} loading={stopping} onClick={onStop}>
            {stale ? "Durumu kapat" : "Durdur"}
          </Button>
        ) : (
          <Button size="sm" icon={Search} loading={starting} disabled={!noSource} onClick={onFindNoSource}>
            {`Kaynaksızlar için ara${findLabel ? ` (${findLabel})` : ""}`}
          </Button>
        )
      }
    >
      {finder && pill ? (
        <div className="grid gap-3">
          <p role="status" className="flex flex-wrap items-center gap-2 text-xs text-neutral-300">
            <TonePill tone={pill.tone} icon={pill.icon} spin={running}>
              {pill.label}
            </TonePill>
            <span>
              {`${scope} · ${done.toLocaleString("tr-TR")} / ${total.toLocaleString("tr-TR")} istasyon · ${String(finder.fixed ?? 0)} istasyona ${String(finder.added ?? 0)} kaynak eklendi`}
            </span>
            <span className="text-neutral-600">
              {finder.startedAt ? `${fmtDate(finder.startedAt)} başladı` : ""}
              {finder.finishedAt && !running ? ` · ${fmtDate(finder.finishedAt)} bitti` : ""}
            </span>
          </p>
          {running && (
            <div className="grid gap-1">
              <ProgressBar pct={total ? (done / total) * 100 : null} />
              {finder.current && <span className="text-2xs text-neutral-600">şu an: {finder.current}</span>}
            </div>
          )}
          {finder.error && (finder.status === "paused" || finder.status === "error") && (
            <p className="text-xs text-warn">{finder.error}</p>
          )}
          <p className="text-2xs text-neutral-600">{autoLine}</p>
          {sorted.length > 0 && (
            <ul role="list" className="grid gap-1 text-xs">
              {shown.map((r) => (
                <li key={r.key} className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="text-left text-neutral-200 hover:text-accent-300 hover:underline"
                    title="Listede göster"
                    onClick={() => onPick(r.name)}
                  >
                    {r.name}
                  </button>
                  {r.added > 0 && (
                    <TonePill tone="ok" icon={CheckIcon}>
                      {`${r.added} kaynak eklendi`}
                    </TonePill>
                  )}
                  {r.review > 0 && (
                    <TonePill tone="warn" icon={EyeOff}>
                      {`${r.review} onay bekliyor`}
                    </TonePill>
                  )}
                  {r.added + r.review === 0 && (
                    <span className="text-2xs text-neutral-600" title={r.error}>
                      {r.error ? "hata" : "bulunamadı"}
                    </span>
                  )}
                </li>
              ))}
              {sorted.length > RESULTS_SHOWN && (
                <li>
                  <Button size="sm" variant="ghost" onClick={() => setShowAll((v) => !v)}>
                    {showAll ? "Daha az göster" : `Tümünü göster (${sorted.length})`}
                  </Button>
                </li>
              )}
            </ul>
          )}
        </div>
      ) : (
        <p className="text-xs text-neutral-500">
          Henüz çalışmadı. Kaynaksızların hepsi için sağdaki düğmeyi kullan ya da listeden istasyon seçip "Seçilenlerde
          kaynak ara"ya bas.
        </p>
      )}
    </Card>
  );
}

/** Listenin üstünde: sayfadakileri seç, seçimi temizle, seçilenlerde ara. */
function SelectionBar({
  pageGroups,
  selected,
  onChange,
  busy,
  blocked,
  onFind,
  canAdd,
  adding,
  onAdd,
  merging,
  onMerge,
  banning,
  onBan,
}: {
  pageGroups: StationGroup[];
  selected: Map<string, string>;
  onChange: (next: Map<string, string>) => void;
  busy: boolean;
  blocked: boolean;
  onFind: () => void;
  canAdd: boolean;
  adding: boolean;
  onAdd: () => void;
  merging: boolean;
  onMerge: () => void;
  banning: boolean;
  onBan: () => void;
}): ReactNode {
  const allOnPage = pageGroups.every((g) => selected.has(g.key));
  const togglePage = (on: boolean): void => {
    const next = new Map(selected);
    for (const g of pageGroups) {
      if (on) next.set(g.key, g.primary.name);
      else next.delete(g.key);
    }
    onChange(next);
  };
  return (
    <div className="flex min-h-10 flex-wrap items-center gap-3 border-t border-neutral-900 px-4 py-2">
      <Check label="Sayfadakileri seç" checked={allOnPage} onChange={togglePage} />
      {selected.size > 0 && (
        <>
          <span className="text-xs text-neutral-400">{selected.size.toLocaleString("tr-TR")} seçili</span>
          <Button size="sm" variant="ghost" onClick={() => onChange(new Map())}>
            Seçimi temizle
          </Button>
          <Button
            size="sm"
            icon={Search}
            loading={busy}
            disabled={blocked || selected.size > 500}
            title={blocked ? "Bir kaynak arama zaten çalışıyor" : selected.size > 500 ? "En fazla 500 istasyon" : undefined}
            onClick={onFind}
          >
            Seçilenlerde kaynak ara
          </Button>
          {selected.size >= 2 && (
            <ConfirmButton
              size="sm"
              variant="secondary"
              icon={Merge}
              confirmLabel={`${selected.size} istasyon tek istasyon olsun mu?`}
              loading={merging}
              disabled={selected.size > 30}
              title={
                selected.size > 30
                  ? "En fazla 30 istasyon"
                  : "Seçilen istasyonların bütün kaynakları tek istasyon olur (elle grup); istasyonu açıp Grup alanını boşaltınca ayrılır"
              }
              onConfirm={onMerge}
            >
              Seçilenleri birleştir
            </ConfirmButton>
          )}
          {canAdd && (
            <ConfirmButton
              size="sm"
              variant="secondary"
              icon={Plus}
              confirmLabel={`${selected.size} istasyon uygulamaya eklensin mi?`}
              loading={adding}
              disabled={selected.size > 200}
              title="Uygulamanın TR listesine ekler; zaten olanlar atlanır (en fazla 200)"
              onConfirm={onAdd}
            >
              Uygulamaya ekle
            </ConfirmButton>
          )}
          <ConfirmButton
            size="sm"
            variant="ghost"
            icon={Ban}
            confirmLabel={`${selected.size} istasyon yasaklansın mı?`}
            loading={banning}
            onConfirm={onBan}
          >
            Seçilenleri yasakla
          </ConfirmButton>
        </>
      )}
    </div>
  );
}

// --- Yasaklananlar -----------------------------------------------------------------

const BAN_KIND_LABEL: Record<StationBanKind, string> = {
  names: "ad",
  hosts: "yayın sunucusu",
  ids: "kayıt kimliği",
};

const normBanName = (s: string): string => s.trim().toLowerCase();

/** Uygulamada olmayan TR istasyonlarını TR listesine ekler; sonucu özetler. */
function useAddToApp(after?: () => void): ReturnType<typeof useMutation<StationAddToAppResult, Error, string[]>> {
  const toast = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (groupKeys: string[]) => client.addStationsToApp(groupKeys),
    onSuccess: (res) => {
      const reasons = res.skipped.map((x) => `${x.name}: ${x.reason}`);
      toast({
        tone: res.added.length ? "ok" : "danger",
        title: res.added.length
          ? `${res.added.length} istasyon uygulamaya eklendi${res.skipped.length ? `, ${res.skipped.length} atlandı` : ""}`
          : "Eklenemedi",
        body: [
          res.added.length ? "Uygulamada en geç 24 saat içinde görünür (liste önbelleği)" : null,
          ...reasons.slice(0, 6),
          reasons.length > 6 ? `…ve ${reasons.length - 6} tane daha` : null,
        ]
          .filter(Boolean)
          .join("\n"),
      });
      void queryClient.invalidateQueries({ queryKey: ["stations"] });
      void queryClient.invalidateQueries({ queryKey: ["stations-state"] });
      after?.();
    },
    onError: (err) => toast({ tone: "danger", title: "Eklenemedi", body: humanizeError(err) }),
  });
}

/** Yasak işlemleri: listeyi, sayıları ve kuralları birlikte tazeler. */
function useBanActions(): {
  ban: ReturnType<typeof useMutation<{ added: string[] }, Error, { kind: StationBanKind; values: string[]; note?: string }>>;
  unban: ReturnType<typeof useMutation<{ removed: number }, Error, { kind: StationBanKind; values: string[] }>>;
} {
  const toast = useToast();
  const queryClient = useQueryClient();
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["station-bans"] });
    void queryClient.invalidateQueries({ queryKey: ["stations"] });
    void queryClient.invalidateQueries({ queryKey: ["stations-state"] });
  };
  const ban = useMutation({
    mutationFn: (v: { kind: StationBanKind; values: string[]; note?: string }) => client.banStations(v.kind, v.values, v.note),
    onSuccess: (res) => {
      toast({
        tone: "ok",
        title: res.added.length ? `${res.added.length} kural eklendi` : "Zaten yasaklı",
        body: res.added.length ? "Uygulamada birkaç dakika içinde kalkar; güncelleme gerekmez" : undefined,
      });
      refresh();
    },
    onError: (err) => toast({ tone: "danger", title: "Yasaklanamadı", body: humanizeError(err) }),
  });
  const unban = useMutation({
    mutationFn: (v: { kind: StationBanKind; values: string[] }) => client.unbanStations(v.kind, v.values),
    onSuccess: () => {
      toast({ tone: "ok", title: "Yasak kaldırıldı", body: "Uygulamada birkaç dakika içinde geri gelir" });
      refresh();
    },
    onError: (err) => toast({ tone: "danger", title: "Kaldırılamadı", body: humanizeError(err) }),
  });
  return { ban, unban };
}

/** "Yasaklanan" filtresinde listenin üstünde: kurallar, ekleme, koddaki filtre. */
function BansPanel({ bans }: { bans: StationBans | undefined }): ReactNode {
  const { ban, unban } = useBanActions();
  const [kind, setKind] = useState<"names" | "hosts">("names");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const rules = bans
    ? (["names", "hosts", "ids"] as const).flatMap((k) =>
        bans[k].map((v) => {
          const key = `${k}:${k === "names" ? normBanName(v) : v.trim().toLowerCase()}`;
          return { kind: k, value: v, meta: bans.meta[key] ?? bans.meta[`${k}:${v}`] };
        }),
      )
    : [];
  rules.sort((a, b) => (b.meta?.at ?? "").localeCompare(a.meta?.at ?? ""));

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    const values = value
      .split(/[\n,]/)
      .map((x) => x.trim())
      .filter(Boolean);
    if (!values.length) return;
    ban.mutate(
      { kind, values, note: note.trim() || undefined },
      {
        onSuccess: () => {
          setValue("");
          setNote("");
        },
      },
    );
  };

  return (
    <div className="grid gap-3 text-xs">
      <p className="text-neutral-500">
        Yasaklanan istasyon uygulamada hiçbir listede görünmez (TR listesi dahil). Birkaç dakika içinde etki eder,
        uygulama güncellemesi gerekmez; kaldırınca geri gelir. Adla yasaklamak istasyonun tüm kaynaklarını kapsar.
      </p>
      <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
        <select
          className="input !w-auto"
          aria-label="Kural türü"
          value={kind}
          onChange={(e) => setKind(e.target.value as "names" | "hosts")}
        >
          <option value="names">Ad</option>
          <option value="hosts">Yayın sunucusu</option>
        </select>
        <input
          className="input min-w-0 flex-1 basis-48"
          aria-label={kind === "names" ? "İstasyon adı" : "Yayın sunucusu"}
          placeholder={kind === "names" ? "istasyon adı (virgülle birden çok)" : "ör. stream.ornekfm.com"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <input
          className="input min-w-0 flex-1 basis-40"
          aria-label="Neden (isteğe bağlı)"
          placeholder="neden (isteğe bağlı)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <Button size="sm" type="submit" icon={Ban} loading={ban.isPending} disabled={!value.trim()}>
          Yasakla
        </Button>
      </form>

      {rules.length ? (
        <ul role="list" className="grid gap-1">
          {rules.map((r) => (
            <li key={`${r.kind}:${r.value}`} className="flex flex-wrap items-center gap-2">
              <span className="tag tag-neutral">{BAN_KIND_LABEL[r.kind]}</span>
              <span className={r.kind === "names" ? "text-neutral-200" : "mono text-neutral-200"}>{r.value}</span>
              <span className="text-2xs text-neutral-600">
                {r.meta ? `${r.meta.by} · ${fmtDate(r.meta.at)}${r.meta.note ? ` · ${r.meta.note}` : ""}` : "elle (flag sayfasından)"}
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="ml-auto"
                loading={unban.isPending && unban.variables?.values[0] === r.value}
                onClick={() => unban.mutate({ kind: r.kind, values: [r.value] })}
              >
                Kaldır
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-neutral-500">Konsoldan yasaklanmış istasyon yok.</p>
      )}

      {bans && (
        <details className="text-neutral-500">
          <summary className="cursor-pointer">Uygulama kodundaki kalıcı filtre (salt okunur)</summary>
          <p className="mt-1">
            Adlar: {bans.code.names.join(", ")} · Kelimeler: {bans.code.words.join(", ")}. Değiştirmek için uygulama
            güncellemesi gerekir. Uygulama bu filtreyi yalnızca radio-browser sonuçlarına (TR dışı ülkeler) uygular;
            TR listesindeki (kürasyon) istasyonlar bu filtreden geçmez, onları buradan yasaklaman gerekir.
          </p>
        </details>
      )}
    </div>
  );
}

type SyncWhat = "radio-browser" | "curated" | "radio-polska" | "chunklist";

/** Senkron ve tarama durumu: nadiren gerekir, listenin altında. */
function DirectoryCard({
  sync,
  counts,
  busy,
  onSync,
}: {
  sync: Record<string, Record<string, unknown>> | undefined;
  counts: { total: number; checked: number; noSource: number } | undefined;
  busy: SyncWhat | null | undefined;
  onSync: (what: SyncWhat) => void;
}): ReactNode {
  const rb = sync?.rbFull;
  const rp = sync?.rpImport;
  const ck = sync?.chunklistClean;
  const health = sync?.health;
  const rbStatus = rb?.status === "running" ? "running" : rb?.status === "ok" ? "succeeded" : rb ? "failed" : null;
  const rpStatus = rp?.status === "running" ? "running" : rp?.status === "ok" ? "succeeded" : rp ? "failed" : null;
  const n = (v: unknown): string => Number(v ?? 0).toLocaleString("tr-TR");
  return (
    <Card
      title="Dizin ve tarama"
      action={
        <>
          <Button size="sm" loading={busy === "curated"} onClick={() => onSync("curated")}>
            TR listesini içe aktar
          </Button>
          <Button size="sm" loading={busy === "radio-browser" || rb?.status === "running"} onClick={() => onSync("radio-browser")}>
            radio-browser senkronu
          </Button>
          <Button
            size="sm"
            loading={busy === "radio-polska" || rp?.status === "running"}
            onClick={() => onSync("radio-polska")}
            title="radio-polska.pl'deki, radio-browser'da olmayan PL istasyonlarını dizine alır; çalışanlar uygulamaya ek istasyon olarak gider"
          >
            radio-polska içe aktar
          </Button>
          <Button
            size="sm"
            loading={busy === "chunklist" || ck?.status === "running"}
            onClick={() => onSync("chunklist")}
            title="chunklist…m3u8 adreslerini sunucunun ana listesine (playlist.m3u8) çevirir; ana liste açılmazsa adrese dokunmaz"
          >
            Chunklist temizle
          </Button>
        </>
      }
    >
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-2 text-xs">
        <dt className="text-neutral-500">radio-browser</dt>
        <dd role="status" className="flex flex-wrap items-center gap-2 text-neutral-300">
          {rbStatus ? <StatusPill status={rbStatus} /> : <span>henüz senkronlanmadı</span>}
          {rb?.status === "ok" &&
            `${fmtDate(String(rb.finishedAt))} · ${Number(rb.fetched).toLocaleString("tr-TR")} kayıt · ${String(rb.markedDeleted)} kalkmış · her gece kendiliğinden`}
          {rb?.status === "running" && `${fmtDate(String(rb.startedAt))} başladı`}
          {rb?.status === "error" && String(rb.error)}
        </dd>
        <dt className="text-neutral-500">radio-polska</dt>
        <dd role="status" className="flex flex-wrap items-center gap-2 text-neutral-300">
          {rpStatus ? <StatusPill status={rpStatus} /> : <span>henüz içe aktarılmadı</span>}
          {rp?.status === "running" && `${n(rp.pages)} / ${n(rp.total)} sayfa · ${n(rp.added)} yeni kaynak`}
          {rp?.status === "ok" &&
            `${fmtDate(String(rp.finishedAt))} · ${n(rp.pages)} sayfa · ${n(rp.added)} yeni, ${n(rp.working)} açıldı, ${n(rp.ownLogo)} kendi logosuyla · ${n(rp.existing)} radio-browser'da zaten var · haftada bir kendiliğinden`}
          {(rp?.status === "error" || rp?.status === "paused") && String(rp.error)}
        </dd>
        <dt className="text-neutral-500">Chunklist</dt>
        <dd role="status" className="flex flex-wrap items-center gap-2 text-neutral-300">
          {ck ? (
            <StatusPill status={ck.status === "running" ? "running" : ck.status === "ok" ? "succeeded" : "failed"} />
          ) : (
            <span>henüz temizlenmedi</span>
          )}
          {ck?.status === "running" && `${fmtDate(String(ck.startedAt))} başladı`}
          {ck?.status === "ok" &&
            `${fmtDate(String(ck.finishedAt))} · ${n(ck.total)} adres, ${n(ck.fixed)} ana listeye çevrildi, ${n(ck.kept)} ana listesi açılmadı · her gece senkrondan sonra`}
          {ck?.status === "error" && String(ck.error)}
        </dd>
        <dt className="text-neutral-500">Sağlık taraması</dt>
        <dd className="text-neutral-300">
          {health
            ? `${health.status === "network-down" ? "ağ yok, bekliyor" : health.status === "idle" ? "sırada iş yok" : "çalışıyor"} · son 24 saat: ${Number(health.checked).toLocaleString("tr-TR")} denendi, ${Number(health.ok).toLocaleString("tr-TR")} açıldı · otomatik gizleme kapalı, gizleme elle`
            : "henüz başlamadı"}
          {counts ? ` · toplam ${counts.checked.toLocaleString("tr-TR")} / ${counts.total.toLocaleString("tr-TR")} kaynak denendi` : ""}
        </dd>
      </dl>
    </Card>
  );
}

/** Filtreye göre satırda gösterilecek kaynak: "Açılmayan"da bozuk olan, vb. */
function focusSource(g: StationGroup, filter: StationFilter): StationListRow {
  const find = (fn: (s: StationListRow) => boolean): StationListRow | undefined => g.sources.find(fn);
  if (filter === "dead") {
    return (
      find(
        (s) =>
          !isHiddenSrc(s) &&
          !(s.deviceOkAt && isUnreachable(s)) &&
          (s.checkOk === false || !!s.deviceDeadAt || (s.checkOk === null && s.rbCheckOk === false)),
      ) ?? g.primary
    );
  }
  if (filter === "rivallogo") return find((s) => !isHiddenSrc(s) && logoOrigin(s.favicon, s.homepage)?.kind === "rival") ?? g.primary;
  if (filter === "autohidden") return find((s) => s.autoHidden) ?? g.primary;
  if (filter === "hidden") return find((s) => s.hidden || s.rbDeleted) ?? g.primary;
  if (filter === "edited") {
    // Onay bekleyen kaynak varsa o gösterilir (satırdan onaylanabilsin).
    const pending = find(isFinderPending);
    if (pending) return pending;
    const edited = g.sources.filter((s) => s.edited || s.editedBy);
    return edited.sort((a, b) => (b.editedAt ?? "").localeCompare(a.editedAt ?? ""))[0] ?? g.primary;
  }
  return g.primary;
}

/**
 * Satır bir düğme DEĞİL: ad ve adres seçilip kopyalanabilsin, adres yeni sekmede
 * açılabilsin. Satırın boş yerine tıklamak düzenlemeyi açar (metin seçerken
 * açılmaz); klavye için sağda ayrı "Düzenle" düğmesi var.
 */
function GroupRow({
  g,
  filter,
  onOpen,
  selected,
  onSelect,
}: {
  g: StationGroup;
  filter: StationFilter;
  onOpen: (id: string) => void;
  selected: boolean;
  onSelect: (on: boolean) => void;
}): ReactNode {
  const toast = useToast();
  const src = focusSource(g, filter);
  const editor = editorOf(src);
  const p = g.primary;
  const maxVotes = g.sources.reduce<number | null>((m, x) => (x.votes != null && (m == null || x.votes > m) ? x.votes : m), null);
  const allHidden = g.sources.every(isHiddenSrc);
  const curated = g.sources.some((x) => x.curated);
  const edited = g.sources.some((x) => x.edited);
  const banned = g.sources.some((x) => x.banned === "flag") ? "flag" : g.sources.some((x) => x.banned === "code") ? "code" : null;
  const meta = [
    p.countrycode,
    src.hls ? "HLS" : src.codec,
    src.bitrate ? `${src.bitrate} kbps` : null,
    g.sources.length > 1 ? `${g.sources.length} kaynak${g.working ? ` · ${g.working} çalışıyor` : ""}` : null,
    g.sources.some((x) => x.pinned) ? "varsayılan sabit" : null,
  ].filter(Boolean);

  const openFromRow = (e: MouseEvent<HTMLDivElement>): void => {
    // Bağlantı/düğme kendi işini yapsın; metin seçiliyse (kopyalamak için) açma.
    if ((e.target as HTMLElement).closest("a, button, input, label")) return;
    if (window.getSelection()?.toString()) return;
    onOpen(src.id);
  };

  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);
  const copyUrl = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(src.url);
      setCopied(true);
      toast({ tone: "ok", title: "Adres kopyalandı", body: src.url });
    } catch {
      toast({ tone: "danger", title: "Kopyalanamadı", body: "Tarayıcı panoya erişime izin vermedi" });
    }
  };

  return (
    <div className="row row-hover group/row flex-wrap items-center gap-3" onClick={openFromRow}>
      <label className="-m-2 grid shrink-0 cursor-pointer place-items-center p-2">
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => onSelect(e.target.checked)}
          aria-label={`${p.name} seç`}
          className="size-3.5 accent-[var(--color-accent)]"
        />
      </label>
      <Logo src={p.favicon} broken={p.logoOk === false} />
      <LogoProposalBlock row={p} />
      <div className="grid min-w-0 flex-1 basis-[220px] gap-0.5">
        <span className="flex flex-wrap items-center gap-2">
          <span className={`cursor-text select-text ${allHidden ? "text-neutral-500 line-through decoration-neutral-700" : "text-neutral-100"}`}>
            {p.name}
          </span>
          {curated && (
            <span className="tag tag-accent">
              kürasyon{maxVotes != null ? <span className="sr-only">, puan</span> : null}
              {maxVotes != null ? ` · ${maxVotes}` : ""}
            </span>
          )}
          {edited && <span className="tag tag-neutral">düzenlendi</span>}
          {allHidden && <span className="tag tag-neutral">gizli</span>}
          <RivalLogoTag origin={logoOrigin(src.favicon, src.homepage)} />
          {g.sources.some((x) => x.hiddenReason === RP_PENDING) && (
            <span className="tag tag-outline" style={{ color: "var(--color-warn)", borderColor: "var(--color-warn)" }}>
              radio-polska · onay bekliyor
            </span>
          )}
          {p.ext?.similarTo && (
            <span className="text-2xs text-warn" title="radio-browser'da benzer adlı istasyon var; aynı istasyonsa alma ya da grupla birleştir">
              benzer: {p.ext.similarTo}
            </span>
          )}
          {!curated && p.countrycode === "TR" && (
            <span className="tag tag-outline" title="Uygulamanın TR listesinde değil">
              uygulamada yok
            </span>
          )}
          {banned && (
            <span className="tag tag-danger" title={banned === "code" ? "Uygulama kodundaki filtreye takılıyor (TR dışı)" : undefined}>
              {banned === "code" ? "kodda yasaklı" : "yasaklı"}
            </span>
          )}
        </span>
        <span className="text-2xs text-neutral-600">
          {meta.join(" · ")}
          <span className="sr-only">. Durum: {healthOf(src).label}</span>
        </span>
        {filter === "edited" && editor && (
          <span className="flex flex-wrap items-center gap-2 text-2xs">
            <span className={`tag ${editor.auto ? "tag-accent" : "tag-neutral"}`}>{editor.auto ? "otomatik" : "elle"}</span>
            <span className="text-neutral-500">{editor.label}</span>
            {src.hidden && (src.hiddenReason ?? "").startsWith("kaynak bulucu") && (
              <span className="text-warn">onay bekliyor</span>
            )}
          </span>
        )}
        <span className="flex min-w-0 items-center gap-1">
          {src.url && <ListenButton url={src.url} name={p.name} compact />}
          <a
            href={src.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mono inline-flex min-w-0 items-center gap-1 text-xs text-neutral-500 hover:text-accent-300 hover:underline"
            title="Yayını yeni sekmede aç"
          >
            <span className="truncate">{src.url}</span>
            <ExternalLink size={11} className="shrink-0" aria-hidden />
          </a>
          <button
            type="button"
            onClick={() => void copyUrl()}
            aria-label={`${p.name} yayın adresini kopyala`}
            title="Adresi kopyala"
            className="grid size-6 shrink-0 place-items-center rounded-[var(--radius-sm)] text-neutral-600 opacity-0 transition-opacity hover:bg-white/5 hover:text-neutral-200 focus-visible:opacity-100 group-hover/row:opacity-100"
          >
            {copied ? <CheckIcon size={13} className="text-ok" aria-hidden /> : <Copy size={13} aria-hidden />}
          </button>
        </span>
      </div>
      {g.sources.some((x) => x.hiddenReason === RP_PENDING) && <ExternalReviewButtons groupKey={g.key} name={p.name} />}
      {isFinderPending(src) && <FinderPendingButtons src={src} />}
      {!src.deviceOkAt && !src.deviceDeadAt && <DeviceVerdictButtons s={src} />}
      {(filter === "nosource" || filter === "nosourcechecked") && (
        <NoSourceButtons groupKey={g.key} name={p.name} checked={filter === "nosourcechecked"} />
      )}
      <span className="flex w-40 shrink-0 justify-end">
        <HealthPill s={src} />
      </span>
      <span className="mono w-14 shrink-0 text-right text-2xs text-neutral-600" title="radio-browser tıklama sayısı (tüm kaynaklar)">
        <span className="sr-only">tıklama: </span>
        {g.clickcount.toLocaleString("tr-TR")}
      </span>
      <span className="flex basis-full justify-end sm:w-[92px] sm:basis-auto">
        <Button
          size="sm"
          variant="ghost"
          icon={Pencil}
          aria-label={`${p.name} düzenle`}
          onClick={() => onOpen(src.id)}
        >
          Düzenle
        </Button>
      </span>
    </div>
  );
}

// --- Düzenleme penceresi -------------------------------------------------------------

function valueOf(s: StationDetail, key: StationEditableField): string {
  if (key === "group") {
    const g = (s.override as Record<string, unknown> | null)?.group;
    return typeof g === "string" ? g : "";
  }
  const v = s[key as keyof StationDetail];
  return v == null ? "" : String(v);
}

/** Düzeltme yoksa görülecek değer: TR listesi, yoksa radio-browser. */
function upstreamOf(s: StationDetail, key: StationEditableField): { value: string; from: string } | null {
  const c = s.curated as Record<string, unknown> | null;
  const r = s.rb as Record<string, unknown> | null;
  const cv = c?.[key];
  if (cv != null && cv !== "") return { value: String(cv), from: "TR listesi" };
  const rv = key === "url" ? (r?.url_resolved ?? r?.url) : r?.[key];
  if (rv != null && rv !== "") return { value: String(rv), from: "radio-browser" };
  return null;
}

function StationModal({
  id,
  onOpen,
  onClose,
}: {
  id: string;
  onOpen: (id: string) => void;
  onClose: () => void;
}): ReactNode {
  const toast = useToast();
  const queryClient = useQueryClient();
  const formId = useId();
  // Kaynak değişince eski içerik yerinde kalsın: odak kaybolmasın, sayfa zıplamasın.
  const detail = useQuery({
    queryKey: ["station", id],
    queryFn: () => client.station(id),
    placeholderData: keepPreviousData,
  });
  const s = detail.data?.station.id === id ? detail.data.station : undefined;
  const siblings = detail.data?.siblings ?? [];
  const group = detail.data?.group;

  const [form, setForm] = useState<Record<string, string>>({});
  const [cleared, setCleared] = useState<Set<StationEditableField>>(new Set());
  const [hidden, setHidden] = useState(false);
  const [hiddenReason, setHiddenReason] = useState("");
  const [keepVisible, setKeepVisible] = useState(false);
  const [logoToAll, setLogoToAll] = useState(true);
  const [mirrorError, setMirrorError] = useState<string | null>(null);

  const reset = (st: StationDetail): void => {
    const next: Record<string, string> = {};
    for (const f of FIELDS) next[f.key] = valueOf(st, f.key);
    setForm(next);
    setCleared(new Set());
    setHidden(st.hidden);
    setHiddenReason(st.hiddenReason ?? "");
    setKeepVisible(st.keepVisible);
    setMirrorError(null);
  };
  useEffect(() => {
    if (s) reset(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s?.id, s?.editedAt, s?.hidden, s?.keepVisible]);

  const changedFields = s
    ? FIELDS.filter((f) => cleared.has(f.key) || (form[f.key] ?? "").trim() !== valueOf(s, f.key))
    : [];
  const invalid = !!s && changedFields.some((f) => fieldProblem(f.key, form[f.key] ?? "") !== null);
  const dirty =
    !!s &&
    (changedFields.length > 0 ||
      hidden !== s.hidden ||
      keepVisible !== s.keepVisible ||
      (hidden && hiddenReason.trim() !== (s.hiddenReason ?? "")));

  const confirmLeave = (): boolean =>
    !dirty || window.confirm("Kaydedilmemiş değişiklikler kaybolacak. Devam edilsin mi?");

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["station"] });
    void queryClient.invalidateQueries({ queryKey: ["stations"] });
    void queryClient.invalidateQueries({ queryKey: ["stations-state"] });
  };

  const reportMirror = (m: StationMirrorResult, title: string): void => {
    if (m.ok === false) {
      setMirrorError(m.error ?? "bilinmeyen hata");
      toast({ tone: "danger", title: `${title}, uygulamaya yansımadı`, body: m.error });
    } else {
      setMirrorError(null);
      toast({
        tone: "ok",
        title,
        body: m.ok
          ? "Uygulamadaki TR listesi güncellendi"
          : "Düzeltme listesine girdi; uygulamalar birkaç dakika içinde alır",
      });
    }
  };

  const save = useMutation({
    mutationFn: async () => {
      if (!s) throw new Error("yüklenmedi");
      const override: Partial<Record<StationEditableField, string | number | null>> = {};
      for (const f of changedFields) {
        const now = (form[f.key] ?? "").trim();
        if (cleared.has(f.key) && now === (upstreamOf(s, f.key)?.value ?? "")) override[f.key] = null;
        else if (now === "") override[f.key] = null;
        else override[f.key] = f.numeric ? Number(now) : now;
      }
      const res = await client.updateStation(id, {
        ...(Object.keys(override).length ? { override } : {}),
        ...(hidden !== s.hidden || (hidden && hiddenReason.trim() !== (s.hiddenReason ?? ""))
          ? { hidden, hiddenReason: hiddenReason.trim() }
          : {}),
        ...(keepVisible !== s.keepVisible ? { keepVisible } : {}),
      });
      // Logo çoğu zaman istasyonun tamamı için doğrudur: diğer kaynaklara da uygula.
      let logoCount = 0;
      if (logoToAll && "favicon" in override && siblings.length) {
        await Promise.all(
          siblings.map((sib) => client.updateStation(sib.id, { override: { favicon: override.favicon ?? null } })),
        );
        logoCount = siblings.length;
      }
      return { ...res, logoCount };
    },
    onSuccess: (res) => {
      reportMirror(res.firestore, res.logoCount ? `Kaydedildi (logo ${res.logoCount + 1} kaynağa)` : "Kaydedildi");
      refresh();
    },
    onError: (err) => toast({ tone: "danger", title: "Kaydedilemedi", body: humanizeError(err) }),
  });

  const resetAll = useMutation({
    mutationFn: () => {
      const override: Partial<Record<StationEditableField, null>> = {};
      for (const k of Object.keys(s?.override ?? {})) override[k as StationEditableField] = null;
      return client.updateStation(id, { override });
    },
    onSuccess: (res) => {
      reportMirror(res.firestore, "Düzeltmeler kaldırıldı");
      refresh();
    },
    onError: (err) => toast({ tone: "danger", title: "Kaldırılamadı", body: humanizeError(err) }),
  });

  const retryMirror = useMutation({
    mutationFn: () => client.mirrorStation(id),
    onSuccess: (res) => reportMirror(res.firestore, "Yeniden denendi"),
    onError: (err) => toast({ tone: "danger", title: "Denenemedi", body: humanizeError(err) }),
  });

  const typedUrl = (form.url ?? "").trim();
  const testingTyped = !!s && typedUrl !== "" && typedUrl !== s.url;
  const check = useMutation({
    mutationFn: () => client.checkStation(id, testingTyped ? typedUrl : undefined),
    onSuccess: (res) => {
      const h = healthOf({ ...res.station, checkOk: res.check.ok, checkError: res.check.error ?? null, autoHidden: false });
      toast({
        tone: res.check.ok ? "ok" : "danger",
        title: res.check.ok ? (res.tested ? "Yazdığın adres çalışıyor" : "Yayın çalışıyor") : `Açılmıyor: ${h.label}`,
        body: res.check.ok
          ? [res.check.kind === "hls" ? "HLS" : res.check.contentType, res.check.bitrate && `${res.check.bitrate} kbps`, res.check.icyName]
              .filter(Boolean)
              .join(" · ")
          : res.check.error,
      });
      if (!res.tested) refresh();
    },
    onError: (err) => toast({ tone: "danger", title: "Denenemedi", body: humanizeError(err) }),
  });

  const find = useMutation({
    mutationFn: () => client.findStationSources(id),
    onSuccess: (r) => {
      toast({
        tone: r.added.length ? "ok" : "info",
        title: r.added.length
          ? `${r.added.filter((a) => !a.review).length} kaynak eklendi${r.added.some((a) => a.review) ? `, ${r.added.filter((a) => a.review).length} onay bekliyor` : ""}`
          : "Çalışan yeni adres bulunamadı",
        body: r.added.length
          ? r.added.map((a) => `${a.via}: ${a.url}`).join("\n")
          : `${r.tried} aday denendi${r.skipped ? ` (${r.skipped})` : ""}`,
      });
      refresh();
    },
    onError: (err) => toast({ tone: "danger", title: "Aranamadı", body: humanizeError(err) }),
  });

  // Sil: pencere istasyonun bir sonraki kaynağına geçer, kaynak kalmadıysa kapanır.
  const remove = useMutation({
    mutationFn: (sid: string) => client.deleteStation(sid),
    onSuccess: (res, sid) => {
      reportMirror(res.firestore, "Kaynak silindi");
      refresh();
      if (sid === id) {
        const next = siblings.find((x) => x.id !== sid);
        if (next) onOpen(next.id);
        else onClose();
      }
    },
    onError: (err) => toast({ tone: "danger", title: "Silinemedi", body: humanizeError(err) }),
  });

  // Kaynak bulucunun emin olamayıp gizli eklediği kaynağı onayla (görünür yap).
  const approve = useMutation({
    mutationFn: (sid: string) => client.updateStation(sid, { hidden: false }),
    onSuccess: (res) => {
      reportMirror(res.firestore, "Kaynak onaylandı");
      refresh();
    },
    onError: (err) => toast({ tone: "danger", title: "Onaylanamadı", body: humanizeError(err) }),
  });

  const pin = useMutation({
    mutationFn: (v: { id: string; pinned: boolean }) => client.updateStation(v.id, { pinned: v.pinned }),
    onSuccess: (res, v) => {
      reportMirror(res.firestore, v.pinned ? "Varsayılan kaynak yapıldı" : "Varsayılan otomatiğe bırakıldı");
      refresh();
    },
    onError: (err) => toast({ tone: "danger", title: "Değiştirilemedi", body: humanizeError(err) }),
  });

  const bans = useQuery({ queryKey: ["station-bans"], queryFn: () => client.stationBans() });
  const { ban, unban } = useBanActions();
  const addToApp = useAddToApp(refresh);
  const canAddToApp = !!s && !!detail.data && !detail.data.group.inApp && s.countrycode === "TR";
  const bannedByName = !!s && !!bans.data?.names.some((n) => normBanName(n) === normBanName(s.name));

  const busy = save.isPending || resetAll.isPending || pin.isPending || remove.isPending;
  // Gizlenince istasyon uygulamadan tamamen kalkar mı: başka görünür, açılan kaynak yoksa.
  const otherUsable = siblings.some((x) => !x.hidden && !x.autoHidden && !x.rbDeleted && x.checkOk !== false);
  const hidingCurated = !!group?.inApp && hidden && !s?.hidden && !otherUsable;

  const submit = (e?: FormEvent): void => {
    e?.preventDefault();
    if (!dirty || invalid || busy) return;
    // Kürasyonlu kaynağı gizlemek istasyonu uygulamadan kaldırır: onay düğmesiyle.
    if (hidingCurated && e) return;
    save.mutate();
  };

  const closeGuarded = (): void => {
    if (confirmLeave()) onClose();
  };

  return (
    <Modal
      title={s?.name ?? detail.data?.station.name ?? "İstasyon"}
      subtitle={s ? <span className="mono text-2xs">{s.id}</span> : undefined}
      onClose={closeGuarded}
      dismissable={!busy}
      width={720}
      footer={
        s ? (
          <>
            {s.override && (
              <ConfirmButton
                variant="ghost"
                className="mr-auto"
                confirmLabel="Hepsi kaldırılsın mı?"
                loading={resetAll.isPending}
                disabled={busy}
                onConfirm={() => resetAll.mutate()}
              >
                Tüm düzeltmeleri kaldır
              </ConfirmButton>
            )}
            <ConfirmButton
              variant="ghost"
              icon={Trash2}
              confirmLabel={siblings.length ? "Silinsin mi?" : "Son kaynak, silinsin mi?"}
              loading={remove.isPending}
              disabled={busy}
              onConfirm={() => remove.mutate(id)}
            >
              Kaynağı sil
            </ConfirmButton>
            {canAddToApp && (
              <ConfirmButton
                variant="secondary"
                icon={Plus}
                confirmLabel="Uygulamaya eklensin mi?"
                loading={addToApp.isPending}
                disabled={busy}
                title="Uygulamanın TR listesine ekler; tüm çalışan kaynakları yedek olarak gider"
                onConfirm={() => addToApp.mutate([s.groupKey])}
              >
                Uygulamaya ekle
              </ConfirmButton>
            )}
            {bannedByName ? (
              <Button
                variant="ghost"
                icon={Ban}
                loading={unban.isPending}
                onClick={() => unban.mutate({ kind: "names", values: [s.name] })}
              >
                Yasağı kaldır
              </Button>
            ) : (
              <ConfirmButton
                variant="ghost"
                icon={Ban}
                confirmLabel="Uygulamadan kalkacak, yasaklansın mı?"
                loading={ban.isPending}
                disabled={!bans.data}
                title="İstasyon adıyla yasaklanır: tüm kaynakları uygulamada hiçbir listede görünmez"
                onConfirm={() => ban.mutate({ kind: "names", values: [s.name] })}
              >
                Yasakla
              </ConfirmButton>
            )}
            <Button icon={Search} loading={find.isPending} onClick={() => find.mutate()} title="Ölü adresin varyasyonları, istasyon sitesi, canliradyodinle.fm (TR) ve radio-polska.pl (PL) taranır (en fazla 1 dk)">
              {find.isPending ? "Aranıyor…" : "Kaynak ara"}
            </Button>
            <Button icon={Activity} loading={check.isPending} onClick={() => check.mutate()}>
              {testingTyped ? "Yazdığın adresi dene" : "Kontrol et"}
            </Button>
            {hidingCurated ? (
              <ConfirmButton
                variant="primary"
                confirmLabel="Uygulamadan kalkacak, emin misin?"
                loading={save.isPending}
                disabled={!dirty || invalid || busy}
                onConfirm={() => save.mutate()}
              >
                Kaydet
              </ConfirmButton>
            ) : (
              <Button
                variant="primary"
                type="submit"
                form={formId}
                loading={save.isPending}
                disabled={!dirty || invalid || busy}
                title={invalid ? "Kırmızı uyarılı alanı düzelt" : undefined}
              >
                Kaydet
              </Button>
            )}
          </>
        ) : undefined
      }
    >
      {detail.isError ? (
        <div className="p-4">
          <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
        </div>
      ) : !detail.data ? (
        <div className="p-4">
          <Empty>Yükleniyor…</Empty>
        </div>
      ) : (
        <form id={formId} onSubmit={submit} className="grid gap-4 p-4" aria-busy={!s || undefined}>
          <SourcePicker
            current={detail.data.station}
            siblings={siblings}
            inApp={!!group?.inApp}
            defaultId={group?.defaultId ?? null}
            pinBusyId={pin.isPending ? pin.variables?.id : approve.isPending ? approve.variables : undefined}
            onPin={(sid, pinned) => pin.mutate({ id: sid, pinned })}
            onApprove={(sid) => approve.mutate(sid)}
            onDelete={(sid, url) => {
              if (window.confirm(`Bu kaynak silinsin mi?\n${url}`)) remove.mutate(sid);
            }}
            onPick={(sid) => {
              if (sid !== id && confirmLeave()) onOpen(sid);
            }}
          />

          {s && (
            <>

              {mirrorError && (
                <Banner
                  tone="danger"
                  kicker="uygulamaya yansımadı"
                  action={
                    <Button size="sm" loading={retryMirror.isPending} onClick={() => retryMirror.mutate()}>
                      Tekrar dene
                    </Button>
                  }
                >
                  Değişiklik dizine kaydedildi ama Firestore'a yazılamadı: {mirrorError}
                </Banner>
              )}

              {s.autoHidden && (
                <Banner
                  tone="warn"
                  kicker="otomatik gizlendi"
                  action={
                    <Button size="sm" loading={check.isPending} onClick={() => check.mutate()}>
                      Tekrar kontrol et
                    </Button>
                  }
                >
                  {s.checkFailStreak} kontrol üst üste açılmadı
                  {s.autoHiddenAt ? ` (${fmtDate(s.autoHiddenAt)})` : ""}. Yayın düzelince kendiliğinden geri açılır.
                </Banner>
              )}

              <dl className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-1.5 text-xs">
                <dt className="text-neutral-500">Durum</dt>
                <dd className="flex flex-wrap items-center gap-2">
                  <HealthPill s={s} />
                  {healthOf(s).detail && <span className="text-neutral-500">{healthOf(s).detail}</span>}
                </dd>
                {s.checkedAt && (
                  <>
                    <dt className="text-neutral-500">Son kontrol</dt>
                    <dd className="text-neutral-300">{fmtDate(s.checkedAt)}</dd>
                  </>
                )}
                {(s.codec || s.bitrate || s.hls) && (
                  <>
                    <dt className="text-neutral-500">Biçim</dt>
                    <dd className="text-neutral-300">
                      {[s.hls ? "HLS" : s.codec, s.bitrate ? `${s.bitrate} kbps` : null].filter(Boolean).join(" · ")}
                    </dd>
                  </>
                )}
                {s.certExpiresAt && (
                  <>
                    <dt className="text-neutral-500">Sertifika</dt>
                    <dd className="text-neutral-300">{fmtDate(s.certExpiresAt)} tarihine kadar</dd>
                  </>
                )}
                <dt className="text-neutral-500">Tıklama</dt>
                <dd className="text-neutral-300">{s.clickcount.toLocaleString("tr-TR")} (radio-browser)</dd>
                {s.editedAt && (
                  <>
                    <dt className="text-neutral-500">Düzenlendi</dt>
                    <dd className="text-neutral-300">{fmtDate(s.editedAt)}</dd>
                  </>
                )}
              </dl>

              <div className="flex items-center gap-3">
                <Logo src={form.favicon || null} size={56} />
                <span className="grid gap-1 text-xs text-neutral-500">
                  {s.logoOk === false ? "Kayıtlı logo yüklenemiyor." : !s.favicon ? "Logo yok." : "Logo önizlemesi"}
                  <LogoOriginNote origin={logoOrigin(form.favicon || null, form.homepage || null)} />
                  {logoOrigin(s.favicon, s.homepage)?.kind === "rival" && s.logoProposal?.status !== "pending" && (
                    <>
                      {s.logoProposal?.status === "none" && (
                        <span>Son aramada sitesinde logo bulunamadı ({fmtDate(s.logoProposal.at)}).</span>
                      )}
                      {s.logoProposal?.status === "rejected" && <span>Önerilen logo reddedilmişti.</span>}
                      <FindLogoButton id={s.id} />
                    </>
                  )}
                </span>
                <LogoProposalBlock row={s} size={56} />
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {FIELDS.map((f) => (
                  <FieldEditor
                    key={f.key}
                    def={f}
                    value={form[f.key] ?? ""}
                    overridden={!cleared.has(f.key) && !!s.override && f.key in s.override}
                    upstream={upstreamOf(s, f.key)}
                    onChange={(v) => setForm((prev) => ({ ...prev, [f.key]: v }))}
                    onReset={() => {
                      setForm((prev) => ({ ...prev, [f.key]: upstreamOf(s, f.key)?.value ?? "" }));
                      setCleared((prev) => new Set(prev).add(f.key));
                    }}
                  />
                ))}
              </div>

              {siblings.length > 0 && (form.favicon ?? "") !== valueOf(s, "favicon") && (
                <Check
                  label={`Logoyu bu istasyonun diğer ${siblings.length} kaynağına da uygula`}
                  checked={logoToAll}
                  onChange={setLogoToAll}
                />
              )}

              <fieldset className="grid gap-2 border-t border-white/5 pt-3">
                <legend className="label mb-1">Görünürlük</legend>
                <Check label="Elle gizle (uygulamada gösterme)" checked={hidden} onChange={setHidden} />
                {hidden && (
                  <Field label="Gizleme nedeni" hint="ör. kaldırma talebi, sertifikası dolmuş">
                    <input className="input" value={hiddenReason} onChange={(e) => setHiddenReason(e.target.value)} />
                  </Field>
                )}
                {hidingCurated && (
                  <Banner tone="warn">
                    Bu istasyonun başka çalışan kaynağı yok. Kaydedince istasyon uygulamadaki TR listesinden kalkar.
                  </Banner>
                )}
              </fieldset>
            </>
          )}
        </form>
      )}
    </Modal>
  );
}

/** Sunucudaki sınır: düzeltme değeri en fazla 500 karakter. */
const MAX_FIELD = 500;

/** Kaydetmeden önce alan sorunu; yoksa null. Sunucu da aynılarını reddeder. */
function fieldProblem(key: StationEditableField, raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  if (key === "favicon" || key === "url" || key === "homepage") {
    if (/^data:/i.test(v)) {
      return key === "favicon"
        ? "Görselin kendisi yapıştırılmış (data:…). Görselin web adresi gerekir: görsele sağ tıkla → “Resim adresini kopyala” (https:// ile başlar). Google Görseller'deki küçük önizlemeler böyle kopyalanır; görseli açıp asıl sayfasından kopyala."
        : "Web adresi gerekir (https:// ile başlar), yapıştırılan şey bir veri.";
    }
    if (!/^https?:\/\//i.test(v)) return "http:// ya da https:// ile başlamalı";
  }
  if (v.length > MAX_FIELD) return `Çok uzun: ${v.length} karakter (en fazla ${MAX_FIELD})`;
  return null;
}

function FieldEditor({
  def,
  value,
  overridden,
  upstream,
  onChange,
  onReset,
}: {
  def: FieldDef;
  value: string;
  overridden: boolean;
  upstream: { value: string; from: string } | null;
  onChange: (v: string) => void;
  onReset: () => void;
}): ReactNode {
  const label = (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {def.label}
      {overridden && <span className="tag tag-accent">düzeltildi</span>}
    </span>
  );
  const problem = fieldProblem(def.key, value);
  const hint = problem ? (
    <span className="text-danger">{problem}</span>
  ) : overridden ? (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className="mono break-all">
        {upstream ? `${upstream.from}: ${upstream.value}` : "kaynakta boş"}
      </span>
      <button type="button" className="text-accent-300 underline-offset-2 hover:underline" onClick={onReset}>
        sıfırla
      </button>
    </span>
  ) : (
    def.hint
  );
  return (
    <div className={def.wide ? "sm:col-span-2" : undefined}>
      <Field label={label} hint={hint}>
        <input
          className={`input ${def.mono ? "mono" : ""}`}
          value={value}
          type={def.numeric ? "number" : "text"}
          inputMode={def.numeric ? "numeric" : undefined}
          min={def.numeric ? 0 : undefined}
          step={def.numeric ? 1 : undefined}
          maxLength={def.key === "countrycode" ? 2 : undefined}
          autoCapitalize={def.key === "countrycode" ? "characters" : undefined}
          spellCheck={def.mono ? false : undefined}
          aria-invalid={problem ? true : undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      </Field>
    </div>
  );
}

/** Adres karşılaştırması (sunucudaki urlKey ile aynı mantık). */
function urlKeyOf(raw: string): string {
  try {
    const u = new URL(raw.trim());
    return `${u.hostname.toLowerCase()}${u.port ? ":" + u.port : ""}${u.pathname.replace(/[/;]+$/, "")}${u.search}`;
  } catch {
    return raw.trim().toLowerCase();
  }
}

type PickerSrc = StationDetail | StationListRow;

/**
 * Aynı istasyonun kaynakları; seçilen kaynak aşağıda düzenlenir. radio-browser'da
 * aynı yayın çoğu zaman defalarca eklenmiş: aynı adres tek satırda "×N" olur.
 */
function SourcePicker({
  current,
  siblings,
  inApp,
  defaultId,
  pinBusyId,
  onPin,
  onApprove,
  onDelete,
  onPick,
}: {
  current: StationDetail;
  siblings: StationListRow[];
  inApp: boolean;
  defaultId: string | null;
  pinBusyId: string | undefined;
  onPin: (id: string, pinned: boolean) => void;
  onApprove: (id: string) => void;
  onDelete: (id: string, url: string) => void;
  onPick: (id: string) => void;
}): ReactNode {
  const labelId = useId();
  if (!siblings.length) return null;
  const groups: { rep: PickerSrc; all: PickerSrc[] }[] = [];
  const byKey = new Map<string, { rep: PickerSrc; all: PickerSrc[] }>();
  // Sunucu sırası = varsayılan olma sırası; seçili kaynak kendi grubunun temsilcisi olsun.
  for (const src of [current, ...siblings]) {
    const k = urlKeyOf(src.url);
    const g = byKey.get(k);
    if (g) g.all.push(src);
    else {
      const ng = { rep: src, all: [src] };
      byKey.set(k, ng);
      groups.push(ng);
    }
  }
  const order = (x: PickerSrc): number => (x.appRole === "default" ? 0 : x.appRole === "backup" ? 1 : 2);
  groups.sort((a, b) => order(a.rep) - order(b.rep));
  return (
    <div className="grid gap-1.5">
      <span className="flex flex-wrap items-baseline justify-between gap-2">
        <span id={labelId} className="label mb-0">
        Kaynaklar ({groups.length} adres{groups.length < siblings.length + 1 ? `, ${siblings.length + 1} kayıt` : ""})
        </span>
        <span className="text-2xs text-neutral-600">
          {inApp
            ? "uygulamada varsayılan çalınır, açılmazsa yedeğe geçilir"
            : "düzenlenirse uygulamaya düzeltme listesiyle gider; açılmazsa yedeğe geçilir"}
        </span>
      </span>
      <div role="group" aria-labelledby={labelId} className="grid gap-1">
        {groups.map(({ rep, all }, i) => {
          const active = all.some((x) => x.id === current.id);
          const src = active ? current : rep;
          const hiddenSrc = isHiddenSrc(src);
          const pinnedSrc = all.find((x) => x.pinned);
          const pinned = !!pinnedSrc;
          // Cihazdan onaylı kaynak da varsayılan olabilir (sunucu sıralaması group.ts rankSources).
          const usable = !hiddenSrc && (src.checkOk !== false || (!!src.deviceOkAt && isUnreachable(src)));
          const awaiting = src.hidden && ((src.hiddenReason ?? "").startsWith("kaynak bulucu") || src.hiddenReason === RP_PENDING);
          const isDefault = all.some((x) => x.id === defaultId);
          const inAppList = all.some((x) => ("curated" in x && x.curated === true) || (!!x.curated && typeof x.curated === "object"));
          return (
            <div
              key={src.id}
              className="flex items-center rounded-[var(--radius-sm)] border hover:bg-white/[0.035]"
              style={{
                borderColor: active ? "var(--color-accent-400)" : "rgba(255,255,255,0.08)",
                background: active ? "color-mix(in srgb, var(--color-accent-400) 8%, transparent)" : undefined,
              }}
            >
            <button
              type="button"
              onClick={() => onPick(src.id)}
              aria-current={active ? "true" : undefined}
              className="grid min-w-0 flex-1 grid-cols-[1.5rem_minmax(0,9rem)_4.5rem_minmax(0,1fr)] items-center gap-2 px-2.5 py-1.5 text-left text-xs"
            >
              <span className="mono text-neutral-500">{String(i + 1).padStart(2, "0")}</span>
              <span className="flex min-w-0 flex-wrap gap-1">
                <HealthPill s={src} />
              </span>
              <span className="text-neutral-400">
                {[src.hls ? "HLS" : src.codec, src.bitrate].filter(Boolean).join(" ") || "?"}
              </span>
              <span className="flex min-w-0 items-center gap-1.5">
                {isDefault && (
                  <span className="tag tag-accent inline-flex shrink-0 items-center gap-1">
                    {pinned && <Pin size={11} aria-hidden />}
                    varsayılan
                  </span>
                )}
                {src.appRole === "backup" && <span className="tag tag-neutral shrink-0">yedek</span>}
                {!isDefault && src.appRole !== "backup" && pinned && (
                  <span className="tag tag-neutral inline-flex shrink-0 items-center gap-1">
                    <Pin size={11} aria-hidden />
                    sabit
                  </span>
                )}
                {awaiting ? (
                  <span className="tag tag-outline shrink-0" style={{ color: "var(--color-warn)", borderColor: "var(--color-warn)" }} title={src.hiddenReason ?? undefined}>
                    onay bekliyor
                  </span>
                ) : (
                  hiddenSrc && <span className="tag tag-neutral shrink-0">gizli</span>
                )}
                {src.source === "radiopolska" && (
                  <span
                    className="tag tag-neutral shrink-0"
                    title={`radio-browser'da yok; radio-polska.pl dizininden içe aktarıldı, uygulamaya ek istasyon olarak gider. Logo: ${
                      src.ext?.logoFrom ? `istasyonun sitesi (${src.ext.logoFrom})` : "sitesinde bulunamadı, logosuz"
                    }`}
                  >
                    {src.ext?.page ? (
                      <a href={src.ext.page} target="_blank" rel="noreferrer noopener" className="hover:underline">
                        radio-polska
                      </a>
                    ) : (
                      "radio-polska"
                    )}
                  </span>
                )}
                {src.ext?.mytunerLogo && (
                  <a
                    href={src.ext.mytunerLogo}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="tag tag-outline shrink-0 hover:underline"
                    title="myTuner sunucusundaki görsel: yalnızca bakmak için, uygulamaya gitmez"
                  >
                    myTuner logosu
                  </a>
                )}
                {src.found && (
                  <span
                    className="tag tag-neutral shrink-0"
                    title={`${src.found.via ?? ""}${src.found.page ? `: ${src.found.page}` : ""}`}
                  >
                    bulundu · {src.found.via === "canliradyodinle" || src.found.via === "radio-polska" ? src.found.via : src.found.via === "homepage" ? "site" : "varyasyon"}
                  </span>
                )}
                {all.length > 1 && (
                  <span className="tag tag-neutral shrink-0" title="radio-browser'da aynı adres birden çok kez kayıtlı">
                    ×{all.length}
                  </span>
                )}
                {inAppList && <span className="sr-only">TR listesinde kaydı var.</span>}
                <span className={`mono min-w-0 truncate ${hiddenSrc ? "text-neutral-500 line-through" : "text-neutral-300"}`}>
                  {src.url}
                </span>
              </span>
            </button>
            {awaiting ? (
              <button
                type="button"
                className="shrink-0 px-2.5 py-1.5 text-2xs text-accent-300 hover:underline disabled:opacity-50"
                disabled={!!pinBusyId}
                onClick={() => onApprove(src.id)}
                title="Bu adres gerçekten bu istasyonun yayını ise onayla; görünür olur ve uygulamaya gider"
              >
                {pinBusyId === src.id ? "…" : "onayla"}
              </button>
            ) : pinned ? (
              <button
                type="button"
                className="shrink-0 px-2.5 py-1.5 text-2xs text-neutral-500 hover:text-neutral-200 disabled:opacity-50"
                disabled={!!pinBusyId}
                onClick={() => onPin(pinnedSrc!.id, false)}
                title={isDefault ? undefined : "Sabitlenen kaynak açılmıyor; şimdilik başka kaynak çalınıyor"}
              >
                {pinBusyId === pinnedSrc!.id ? "…" : "sabitlemeyi kaldır"}
              </button>
            ) : usable && !isDefault ? (
              <button
                type="button"
                className="shrink-0 px-2.5 py-1.5 text-2xs text-accent-300 hover:underline disabled:opacity-50"
                disabled={!!pinBusyId}
                onClick={() => onPin(src.id, true)}
              >
                {pinBusyId === src.id ? "…" : "varsayılan yap"}
              </button>
            ) : null}
            <span className="flex shrink-0 items-center px-1.5 py-1">
              <DeviceVerdictButtons s={src} />
            </span>
            <button
              type="button"
              className="shrink-0 px-2 py-1.5 text-2xs text-neutral-600 hover:text-danger disabled:opacity-50"
              disabled={!!pinBusyId}
              onClick={() => onDelete(src.id, src.url)}
              aria-label={`Kaynağı sil: ${src.url}`}
            >
              sil
            </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
